'use strict';

import React from 'react';
import ReactDOM from 'react-dom';
import { select } from 'd3-selection';
import { scaleLinear } from 'd3-scale';
import { max, min } from 'd3-array';
import {
  formatTime,
  formatDistance,
  decimalPlaces,
  getURLParameter,
  hasBetaAccess,
  getUniqueTemporaryID,
  metersPerSecondAsSecondsPerMile,
  metersPerSecondAsSecondsPerKilometer
} from '../common/helpers.js';
import { store } from '../common/redux_store.js';
import {
  STEP_CONSTANTS,
  SWIM_STROKES,
  SWIM_EQUIPMENT,
  SWIM_EQUIPMENT_INACTIVE,
  SWIM_EQUIPMENT_NO_EQUIPMENT,
  INTENSITY,
  WORKOUT_DISCIPLINES,
  WorkoutStep,
  organiseSortedStepsAsNestedSteps,
} from '../models/training-sessions.js';
import activateConfirmationBox from './confirmation-box.jsx'
import activateErrorPopup from './error-popup.jsx'
import * as Action from '../common/redux_actions.js';
import { COLOURS } from '../common/constants.js';
import { axisLeft, axisBottom } from 'd3-axis';
import { DurationInput } from './duration-input.jsx';
import { ResizableTextarea } from './resizable-textarea.jsx'
import AutosizeInput from '../libraries/react-input-autosize.js';
import get_text_as_jsx from '../common/get_text_as_jsx.jsx';
import { ajaxRequest } from '../common/ajax.js'
import interact from '../common/interact-import.js'

// NOTE: add open_workout=yes to URL to help make development easier on page refresh.

const INPUT_BACKGROUND_COLOR = '#dadada';

const dragMoveListener = function (event) {

  // Grab the previous position and update based on the change in position.
  var x = (parseFloat(event.target.getAttribute('data-x')) || 0) + event.dx;
  var y = (parseFloat(event.target.getAttribute('data-y')) || 0) + event.dy;

  // Move the element relative to its starting position.
  var transform = 'translate(' + x + 'px, ' + y + 'px)';
  event.target.style.transform = transform;
  event.target.style.msTransform = transform;
  event.target.style.webkitTransform = transform;

  // Update the position attributes so this can be referred to again.
  event.target.setAttribute('data-x', x);
  event.target.setAttribute('data-y', y);

}

const dragEndListener = (event) => {

  // Reset transform location.
  // This needs to be 'none' rather than setting to 0 because otherwise the
  // drop down menu ends up in the wrong place.
  let transform = 'none';
  event.target.style.transform = transform;
  event.target.style.msTransform = transform;
  event.target.style.webkitTransform = transform;
  event.target.setAttribute('data-x', 0);
  event.target.setAttribute('data-y', 0);

}

export class StructuredTrainingButton extends React.Component {

  handleCreateStructuredWorkout = () => {
    let activity_plan = this.props.training_session.createNewActivityPlan(this.props.discipline);
    store.dispatch(Action.receiveActivityPlans([activity_plan]));
    this.props.openStructuredWorkout(activity_plan, this.props.training_session);
  }

  render () {
      return (
        <div
          className="small-button training_session_form__create_structured_workout_button"
          onClick={this.handleCreateStructuredWorkout}>
            create structured workout
        </div>
      );
  }
}

export class StructuredTrainingSection extends React.Component {

  state = {
    unexpected_error: false,
  }

  static getDerivedStateFromError(error) {
    return { unexpected_error: true };
  }

  handleOpenZoneSettings = () => {
    window.open(this.props.athlete_data.getEditZonesPath(this.props.perspective), '_blank');
  }

  render () {

    if (this.state.unexpected_error) {
      return <div style={{lineHeight: '1.3em'}}>Sorry, there was a problem showing this section due to a bug. If this problem continues please let us know so we can fix it.</div>;
    }

    let activity_plans = [];

    if (this.props.activity_plans && this.props.training_session.activity_plan_uuids) {
      for (let i = 0; i < this.props.training_session.activity_plan_uuids.length; i++) {
        activity_plans.push(
          <StructuredWorkoutDisplay
            key={i}
            activity_plan={this.props.activity_plans[this.props.training_session.activity_plan_uuids[i]]}
            training_session={this.props.training_session}
            training_zones={this.props.training_zones}
            discipline={this.props.discipline}
            formChanged={this.props.formChanged}
            perspective={this.props.perspective}
            has_edit_access={this.props.has_edit_access}
            athlete_data={this.props.athlete_data}
            has_premium_features={this.props.has_premium_features}
            openStructuredWorkout={this.props.openStructuredWorkout}
            handleOpenZoneSettings={this.handleOpenZoneSettings}
          />
        );
      }
    }
    else if (this.props.has_edit_access && this.props.has_premium_features) {
    }
    else {
      return null;
    }

    return (
      <div>
        {activity_plans}
      </div>
    );
  }
}

function handleGarminButtonClick(activity_plan_id, training_session_id, athlete_id) {
  activateConfirmationBox(
    {
      message: "Your Garmin account is linked with Xhale for automatic synchronization of your next 7 days of training. If it doesn't appear to have synced, try pressing this button manually and then refreshing the data in your Garmin app.",
      confirm_callback: (closeConfirmationCallback) => {
        ajaxRequest({
          url: '/garmin/manual-sync-workout/',
          data: {
            activity_plan_id: activity_plan_id,
            training_session_id: training_session_id,
            athlete_id: athlete_id,
          },
          method: 'POST',
          success: (result, status_code) => {
            if (result.result == 'permissions_error') {
              activateErrorPopup(<span>There was a permission error trying to sync Garmin. Please go to your <a href="https://connect.garmin.com/modern/settings/accountInformation" target="_blank">Garmin Connect settings</a> and ensure you have given Xhale permission to sync your training.</span>);
              closeConfirmationCallback();
            }
            else {
              closeConfirmationCallback();
            }
          },
          send_as_form_values: true,
          expected_response_type: 'json',
        });
      },
      yes_button_text: "Sync training",
      no_button_text: "Cancel",
      show_saving_message: "Syncing training to Garmin",
    }
  )
}

function handleCorosButtonClick() {
  activateConfirmationBox(
    {
      message: "Your COROS account is linked with Xhale for automatic synchronization of your next 7 days of training. If it doesn't appear to have synced, try pressing this button manually and then refreshing the data in your COROS app.",
      confirm_callback: (closeConfirmationCallback) => {
        ajaxRequest({
          url: '/coros/push-training-schedule/',
          method: 'POST',
          success: (result, status_code) => {
            closeConfirmationCallback();
          },
          expected_response_type: 'json',
        });
      },
      yes_button_text: "Sync training",
      no_button_text: "Cancel",
      show_saving_message: "Syncing training to COROS",
    }
  )
}

export class StructuredWorkoutDisplay extends React.Component {

  handleOverrideStructuredWorkout = () => {
    this.props.openStructuredWorkout(this.props.activity_plan, this.props.training_session);
    if (this.props.activity_plan.training_session_primary_id != this.props.training_session.id) {
      // Overriding means we have made a change to the session.
      this.props.formChanged();
    }
  }

  handleCorosButtonClick = () => {
    handleCorosButtonClick();
  }

  handleGarminButtonClick = () => {
    handleGarminButtonClick(
      this.props.activity_plan.id,
      this.props.training_session.id,
      this.props.athlete_data.id
    );
  }

  componentDidMount = () => {

    // This code loads the structured workout as soon as the workout is
    // opened. We don't want this to happen every time, but it is handy for
    // development when the page reloads many times. Just add
    // open_workout=yes to the URL parameters.
    if (getURLParameter('open_workout')) {
      this.props.openStructuredWorkout(this.props.activity_plan, this.props.training_session);
    };

  }

  render() {

    var nested_steps = this.props.activity_plan.getStepsBySection();
    var step_sections = [];

    for (var i = 0; i < nested_steps.length; i++) {

      step_sections.push(
        <div className={"step-section step-section-" + i} key={i} style={{ margin: '0 0 8px 0'}}>
          <StructuredTrainingSectionDisplay
            key={i}
            section={nested_steps[i]}
            discipline={this.props.activity_plan.discipline}
            athlete_data={this.props.athlete_data}
            distance_unit_preference={this.props.training_session.getDistanceUnit()}
            handleOpenZoneSettings={this.props.handleOpenZoneSettings}
          />
        </div>
      );

    }

    let graph = <Graph
      context="preview"
      duration_type={this.props.activity_plan.getDurationTypeFromSteps(this.props.activity_plan.workout_steps)}
      activity_or_section="activity"
      activity_or_section_id={this.props.activity_plan.id || 'new'}
      discipline={this.props.activity_plan.discipline}
      distance_unit_preference={this.props.training_session.getDistanceUnit()}
      nested_steps={nested_steps}
      athlete_data={this.props.athlete_data}
      height="140px"
    />

    let edit_or_override_button;
    if (this.props.has_edit_access) {
      let edit_or_override_button_text = (this.props.activity_plan.training_session_primary_id != this.props.training_session.id) ? 'override workout' : 'edit workout'; // Perhaps override workout from 17th August (or from template).
      edit_or_override_button = (
        <div style={{textAlign: 'center', padding: '8px'}}>
          <span className="small-button structured-workout-summary__edit-button" onClick={this.handleOverrideStructuredWorkout}>{edit_or_override_button_text}</span>
        </div>
      );
    }

    let download_buttons;
    if (this.props.printer_view) {
      // Do not display buttons in this view.
    }
    else if (this.props.training_session.tags_hidden.indexOf('premium_only_workout') > -1 && !this.props.has_premium_features) {
      download_buttons = (
        <div className="buttons" style={{background: '#fbb244', padding: '5px', fontWeight: 'bold', fontSize: '12px', textAlign: 'center'}}>
          <span><a href="/premium/">Sign up to premium</a> to download this workout.</span>
        </div>
      );
    }
    else if (!isNaN(this.props.activity_plan.id) && this.props.athlete_data) {

      let fit_button;
      let zwo_button;
      let erg_button;
      let mrc_button;
      let coros_button;
      let garmin_button;

      const fit_export_status = this.props.activity_plan.checkExportStatusAsFIT(
        this.props.training_session,
        this.props.athlete_data?.bike_ftp,
        this.props.training_zones,
        this.props.discipline
      );

      const zwo_export_status = this.props.activity_plan.checkExportStatusAsZWO(
        this.props.training_session,
        this.props.athlete_data?.bike_ftp,
        this.props.training_zones,
        this.props.discipline
      );

      const erg_export_status = this.props.activity_plan.checkExportStatusAsERG(
          this.props.training_session,
          this.props.athlete_data?.bike_ftp,
          this.props.training_zones,
          this.props.discipline
      );

      const mrc_export_status = this.props.activity_plan.checkExportStatusAsMRC(
        this.props.training_session,
        this.props.athlete_data?.bike_ftp,
        this.props.training_zones,
        this.props.discipline
      );

      if (zwo_export_status.can_export) {
        zwo_button = <a className="small-button" href={this.props.activity_plan.zwoDownloadURL(this.props.training_session.id)} target="_blank">zwo</a>;
      } else {
        zwo_button = <span className="small-button small-button_disabled" title={zwo_export_status.description}>zwo</span>;
      }

      if (erg_export_status.can_export) {
        erg_button = <a className="small-button" href={this.props.activity_plan.ergDownloadURL(this.props.training_session.id)} target="_blank" >erg</a>;
      }
      else {
        erg_button = <span className="small-button small-button_disabled" title={erg_export_status.description}>erg</span>;
      }

      if (mrc_export_status.can_export) {
        mrc_button = <a className="small-button" href={this.props.activity_plan.mrcDownloadURL(this.props.training_session.id)} target="_blank">mrc</a>;
      } else {
        mrc_button = <span className="small-button small-button_disabled" title={mrc_export_status.description}>mrc</span>;
      }

      if (fit_export_status.can_export) {
        fit_button = <a className="small-button" href={this.props.activity_plan.fitDownloadURL(this.props.training_session.id)} target="_blank">fit</a>;
      }
      else {
        fit_button = <span className="small-button small-button_disabled" title={fit_export_status.description}>fit</span>;
      }

      if (this.props.athlete_data.coros_connection) {
        coros_button = <span className="small-button" onClick={this.handleCorosButtonClick}>coros</span>
      }

      if (this.props.athlete_data.garmin_connection) {
        garmin_button = <span className="small-button" onClick={this.handleGarminButtonClick}>garmin</span>
      }

      download_buttons = (
        <div className="buttons" style={{float: 'right'}}>
          <span style={{fontSize: '11px', }}>download workout</span>&nbsp;
            <span className="small-buttons-group">{garmin_button}{coros_button}{fit_button}{zwo_button}{erg_button}{mrc_button}</span> <a className="small-button" href="#help=structured-workouts">?</a>
        </div>
      );
    }

    const training_session_discipline_category = this.props.training_session.getDisciplineCategory();
    let mismatched_discipline_warning;
    if (
      training_session_discipline_category == 'swim' && this.props.activity_plan.discipline != WORKOUT_DISCIPLINES.SWIMMING ||
      training_session_discipline_category == 'bike' && this.props.activity_plan.discipline != WORKOUT_DISCIPLINES.CYCLING ||
      training_session_discipline_category == 'run' && this.props.activity_plan.discipline != WORKOUT_DISCIPLINES.RUNNING
    ) {
      mismatched_discipline_warning = (
        <div className="structured-workout-summary__mismatched-discipline-warning" style={{fontSize: '0.9em', marginTop: '1em', textAlign: 'center', color: '#ec760d'}}>
          Warning: training session discipline and this workout are set to a different sport.
        </div>
      );
    }

    return (
      <div className="structured-workout-summary training-plan-section-block" style={{background: 'white', padding: '5px' }}>
        {download_buttons}
        <div style={{clear: 'both'}}>{step_sections}</div>
        {graph}
        {mismatched_discipline_warning}
        {edit_or_override_button}
      </div>
    );

  }
}


export class StructuredWorkoutEditor extends React.Component {

  STEP_SECTION_CSS_SELECTOR = '.step-section';
  STEP_DRAG_HANDLE_CSS_SELECTOR = '.step-section__step__drag-handle';
  STEP_SECTION_DRAG_HANDLE_CSS_SELECTOR = '.step-section__drag-handle'

  constructor(props) {
    super(props);

    this.workoutScrollableSection = React.createRef();
    this.state = {
      active_step_id: null,
      active_template_section: null,
      open_section_menu: null,
      target_type_category: this.getTargetTypeCategory(),
    };

  }

  setActiveStep = (step_id) => {
    this.setState({active_step_id: step_id});
  }

  componentDidUpdate(prevProps, prevState) {
    this.ensureRepeatSectionsExist();

    // The wrong target type can be set if the discipline of the session is
    // incorrect. For some reason examining the discipline_id key directly
    // does not see to differ between props and prevProps even though the
    // training session object itself is different.
    if (this.props.training_session != prevProps.training_session) {
      let target_type_category = this.getTargetTypeCategory();
      if (target_type_category != prevState.target_type_category && !this.isValidTargetTypeCategory()) {
        this.setState({target_type_category: target_type_category});
      }
    }

  }

  componentDidMount = () => {
    this.ensureRepeatSectionsExist();
    this.addDragSectionBehaviour();
    this.addDragStepBehaviour();
    this.addDropBehaviour();
    if (hasBetaAccess('structured_training_section_templates')) {
      this.loadSectionTemplates();
    }
  }

  componentWillUnmount = () => {
    interact(this.STEP_SECTION_CSS_SELECTOR).unset();
    interact(this.STEP_DRAG_HANDLE_CSS_SELECTOR).unset();
    interact(this.STEP_SECTION_DRAG_HANDLE_CSS_SELECTOR).unset();
    this.section_droppable_spacer.remove();
  }

  addDropBehaviour = () => {

    let self = this;

    // Add a drop marker to the DOM.
    self.section_droppable_spacer = document.createElement('div');
    self.section_droppable_spacer.classList.add('droppable-section-spacer');
    self.section_droppable_spacer.style.width = '100%';
    self.section_droppable_spacer.style.height = '5px';
    self.section_droppable_spacer.style.background = '#99e5f3';

    if (!interact) {
      return;
    }

    interact(self.STEP_SECTION_CSS_SELECTOR).dropzone({

      checker: function (dragEvent, event, dropped, dropzone, dropElement, draggable, draggableElement) {
        if (!(
          dragEvent.currentTarget.classList.contains('step-section') ||
          dragEvent.currentTarget.classList.contains('step-section__step') ||
          dragEvent.currentTarget.classList.contains('structured-workout__section-template-marker')
        )) {
          return false;
        }
        else {
          return dropped;
        }
      },

      ondragenter: function (event) {

        event.target.appendChild(self.section_droppable_spacer);

        // Determine the position of any steps. These are then used to
        // determine the order of the steps. The center point of the y
        // position is recorded so that the order is determined based on
        // the edge it is nearest to.
        var step_y_midpoints = [];
        event.target.querySelectorAll(".step-section__step").forEach(function (element) {
          let bounding_client_rect = element.getBoundingClientRect();
          let step_midpoint = (bounding_client_rect.top + bounding_client_rect.bottom) / 2;
          step_y_midpoints.push(step_midpoint);
        });

        event.interaction.step_y_midpoints = step_y_midpoints;

      },

      ondragleave: function (event) {
        self.section_droppable_spacer.remove();
      },

      ondropdeactivate: function (event) {
        self.section_droppable_spacer.remove();
      },

      ondropmove: function (event) {

        let y_cursor_position = event.dragEvent.clientY;

        if (event.dragEvent.currentTarget.classList.contains('step-section__step')) {

          if (!event.interaction.step_y_midpoints) {
            return;
          }

          // Determine what position the dropped element would land in amongst
          // other steps in this section.
          var position = 1;
          for (let i = 0; i < event.interaction.step_y_midpoints.length; i++) {
            if (y_cursor_position > event.interaction.step_y_midpoints[i]) {
              position = i + 2; // Position starts at 1 and we add a second one to make it after the current value.
            }
          }

          // Add the spacer div marker.
          let step_elements = event.target.querySelectorAll('.step-section__step');
          let relative_step_index = null;
          let before_or_after = null;
          if (step_elements.length > 0) {
            if (position == 1) {
              let first_step_element = step_elements[0];
              first_step_element.parentNode.insertBefore(self.section_droppable_spacer, first_step_element);
              relative_step_index = parseInt(first_step_element.getAttribute('data-step-index'));
              before_or_after = 'before';
            }
            else {
              let previous_step_element = step_elements[position - 2];
              if (previous_step_element) {
                previous_step_element.parentNode.insertBefore(self.section_droppable_spacer, previous_step_element.nextSibling);
                relative_step_index = parseInt(previous_step_element.getAttribute('data-step-index'));
                before_or_after = 'after';
              }
            }
          }
          else {
            event.target.querySelector('.step-section__steps').append(self.section_droppable_spacer);
            relative_step_index = parseInt(event.target.querySelector('.step-section__repeat_data').getAttribute('data-section-repeat-step-index'));
            before_or_after = 'before';
          }

          // Store the last updated position for reference when dropped.
          event.interaction.step_final_position = {
            relative_step_index: relative_step_index,
            before_or_after: before_or_after,
          }

        }
        else {
          // Find the midpoint of the section based on the top and bottom positions.
          let bounding_client_rect = event.target.getBoundingClientRect();
          let drop_section_midpoint = (bounding_client_rect.top + bounding_client_rect.bottom) / 2;

          // If the drag y_cursor_position is higher than the midpoint, place the
          // dragged section before the one we are hovering over. If it is below
          // midpoint, place the section after.
          let before_or_after = (y_cursor_position > drop_section_midpoint) ? 'after' : 'before';

          event.interaction.session_final_position = {
            dropped_section_index: parseInt(event.target.getAttribute('data-section-index')),
            relative_position: before_or_after,
          };

          // Add the spacer div marker.
          if (before_or_after == 'before') {
            event.target.parentNode.insertBefore(self.section_droppable_spacer, event.target);
          }
          else if (before_or_after == 'after') {
            event.target.parentNode.insertBefore(self.section_droppable_spacer, event.target.nextSibling);
          }

        }

      },

      ondrop: function (event) {

        if (event.dragEvent.currentTarget.classList.contains('step-section__step')) {
          let dragged_step_index = event.dragEvent.currentTarget.getAttribute('data-step-index');
          let relative_step_index = event.interaction.step_final_position.relative_step_index;
          self.handleMoveStep(dragged_step_index, relative_step_index, event.interaction.step_final_position.before_or_after);
        }
        else if (event.dragEvent.currentTarget.classList.contains('structured-workout__section-template-marker')) {
          const dragged_section_id = event.dragEvent.currentTarget.getAttribute('data-section_id');
          const template_section = self.fetchTemplateSection(dragged_section_id);
          self.addSectionToWorkout(
            template_section,
            event.interaction.session_final_position.dropped_section_index,
            event.interaction.session_final_position.relative_position
          );
        }
        else {
          let dragged_section_index = event.dragEvent.currentTarget.getAttribute('data-section-index');
          self.handleMoveRepeatSection(
            dragged_section_index,
            event.interaction.session_final_position.dropped_section_index,
            event.interaction.session_final_position.relative_position
          );
        }

      }

    });

  }

  addDragStepBehaviour = () => {

    let self = this;

    interact(self.STEP_DRAG_HANDLE_CSS_SELECTOR)
      .draggable({
        manualStart: true,
        onmove: dragMoveListener,
        onend: dragEndListener,
      })
      .on('move', (event) => {

        // Start the drag operation instantly when interacting with a mouse.
        // The check for the correct mouse button stops a right contextual
        // click from accidentally starting the drag operation. The check
        // for undefined makes sure this works in Safari version 10.
        if (event.pointerType == 'mouse' && event.interaction.pointerIsDown && !event.interaction.interacting() && (typeof event.buttons == 'undefined' || event.buttons == 1)) {

          // Start interaction targeting the entire section.
          event.interaction.start({ name: 'drag' }, event.interactable, event.target.closest('.step-section__step'));

        }

      });
  }

  addDragSectionBehaviour = () => {

    let self = this;

    interact(self.STEP_SECTION_DRAG_HANDLE_CSS_SELECTOR)
      .draggable({
        manualStart: true,
        onmove: dragMoveListener,
        onend: dragEndListener,
      })
      .on('move', (event) => {

        // Start the drag operation instantly when interacting with a mouse.
        // The check for the correct mouse button stops a right contextual
        // click from accidentally starting the drag operation. The check
        // for undefined makes sure this works in Safari version 10.
        if (event.pointerType == 'mouse' && event.interaction.pointerIsDown && !event.interaction.interacting() && (typeof event.buttons == 'undefined' || event.buttons == 1)) {

          // Start interaction targeting the entire section.
          event.interaction.start({ name: 'drag' }, event.interactable, event.target.closest('.step-section'));

        }

      });

  }

  loadSectionTemplates = () => {
    if (!this.props.workout_sections_loaded) {
      this.props.fetchWorkoutSections();
    }
  }

  ensureRepeatSectionsExist = () => {
    let steps = this.props.activity_plan.getStepsAsSortedList();

    let first_step_index_to_repeat = null;
    let last_step_index_to_repeat = null;
    for (let i = steps.length - 1 ; i >= 0; i--) {

      if (i == 0 || (steps[i].isRepeatStep() && first_step_index_to_repeat !== null)) {
        break;
      }

      if (!steps[i].isRepeatStep() && !steps[i].repeat_step_index) {
        first_step_index_to_repeat = steps[i].index;
        if (!last_step_index_to_repeat) {
          last_step_index_to_repeat = steps[i].index;
        }
      }

    }

    if (first_step_index_to_repeat !== null && last_step_index_to_repeat !== null) {
      this.handleAddRepeatStep(first_step_index_to_repeat, last_step_index_to_repeat);
    }
    else if (steps.length == 0) {
      this.handleAddRepeatStep(null, -1);
    }

  }

  getDurationType = () => {
    if (this.duration_type) {
      return this.duration_type;
    }

    return this.props.activity_plan.getDurationTypeFromSteps(this.props.activity_plan.workout_steps);
  }

  isValidTargetTypeCategory = () => {
    if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.SWIMMING) {
      if (this.state.target_type_category == 'pace') {
        return true;
      }
    }
    else if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.CYCLING) {
      if (this.state.target_type_category == 'power') {
        return true;
      }
    }
    else if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.RUNNING) {
      if (this.state.target_type_category == 'pace' || this.state.target_type_category == 'heart rate') {
        return true
      }
    }

    return false;
  }

  getTargetTypeCategory = () => {

    // Select a target type category based on the existing step's target types.

    if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.SWIMMING) {
      return 'pace';
    }
    else if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.CYCLING) {
      return 'power';
    }
    else if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.RUNNING) {

      let step_indexes = this.props.activity_plan.workout_steps ? Object.keys(this.props.activity_plan.workout_steps) : [];

      for (let i = 0; i < step_indexes.length; i++) {

        let step_index = step_indexes[i];

        if (this.props.activity_plan.workout_steps[step_index].target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED) {
          return 'pace';
        }
        else if (this.props.activity_plan.workout_steps[step_index].target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE) {
          return 'heart rate';
        }

      }

      // Default to pace.
      return 'pace';

    }

  }

  getTargetType = (repeat_step_index) => {

    // Select a target type based on the last step's target types.
    let nested_steps_starting_order = this.props.activity_plan.getStepsBySection();
    for (let repeats_index = 0; repeats_index < nested_steps_starting_order.length; repeats_index++) {

      if (nested_steps_starting_order[repeats_index].repeat_step_data.index == repeat_step_index) {

        let steps_reversed = nested_steps_starting_order[repeats_index].steps.slice().reverse();

        for (let step_index = 0; step_index < steps_reversed.length; step_index++) {

          // Ignore rest steps.
          if (steps_reversed[step_index].intensity == INTENSITY.REST) {
            continue;
          }

          if (
            steps_reversed[step_index].target_type == STEP_CONSTANTS.TARGET_TYPE_OPEN ||
            steps_reversed[step_index].target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE ||
            steps_reversed[step_index].target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED ||
            steps_reversed[step_index].target_type == STEP_CONSTANTS.TARGET_TYPE_POWER ||
            steps_reversed[step_index].target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_PERCENTAGE_FTP ||
            steps_reversed[step_index].target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE
          ) {
            return steps_reversed[step_index].target_type;
          }

        }

        break;
      }

    }

    // Provide a default target type.
    if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.SWIMMING) {
      return STEP_CONSTANTS.TARGET_TYPE_OPEN;
    }
    else if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.CYCLING) {
      return STEP_CONSTANTS.TARGET_TYPE_POWER;
    }
    if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.RUNNING) {
      if (this.state.target_type_category == 'heart rate')  {
        return STEP_CONSTANTS.TARGET_TYPE_HEART_RATE
      }
      else {
        return STEP_CONSTANTS.TARGET_TYPE_SPEED;
      }
    }

  }

  toggleTargetTypeCategory = () => {
    let new_target_type_category = null;
    if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.RUNNING) {
      switch (this.state.target_type_category) {
        case 'pace':
          new_target_type_category = 'heart rate';
          break;
        case 'heart rate':
          new_target_type_category = 'pace';
          break;
      }
    }

    if (new_target_type_category) {
      this.setState({target_type_category: new_target_type_category});
      this.changeTargetTypeCategory(new_target_type_category);
    }

  }

  toggleDurationType = () => {
    let current_duration_type = this.getDurationType();
    let new_duration_type = (current_duration_type == STEP_CONSTANTS.DURATION_TYPE_TIME) ? STEP_CONSTANTS.DURATION_TYPE_DISTANCE : STEP_CONSTANTS.DURATION_TYPE_TIME;
    this.changeDurationType(new_duration_type);
  }

  toggleDisciplineType = () => {

    if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.SWIMMING) {
      this.props.activity_plan.discipline = WORKOUT_DISCIPLINES.CYCLING;
    }
    else if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.CYCLING) {
      this.props.activity_plan.discipline = WORKOUT_DISCIPLINES.RUNNING;
    }
    else {
      if (hasBetaAccess('structured_training_swimming')) {
        this.props.activity_plan.discipline = WORKOUT_DISCIPLINES.SWIMMING;
      }
      else {
        this.props.activity_plan.discipline = WORKOUT_DISCIPLINES.CYCLING;
      }
    }

    store.dispatch(Action.receiveActivityPlans([this.props.activity_plan]));
    this.props.formChanged();

  }

  /**
   * Cycle through the existing steps and change the target type so that they
   * all are appropriate for the main target type category. This should
   * preserve values well enough that the user can cycle back again to their
   * original target without losing data
   */
  changeTargetTypeCategory = (new_target_type_category) => {

    let new_target_type;
    let new_target_type_zone;
    if (new_target_type_category == 'heart rate') {
      new_target_type = STEP_CONSTANTS.TARGET_TYPE_HEART_RATE;
      new_target_type_zone = STEP_CONSTANTS.TARGET_TYPE_HEART_RATE_BY_ZONE;
    }
    else {
      new_target_type = STEP_CONSTANTS.TARGET_TYPE_SPEED;
      new_target_type_zone = STEP_CONSTANTS.TARGET_TYPE_SPEED_BY_ZONE;
    }

    let altered_steps = [];
    Object.keys(this.props.activity_plan.workout_steps).forEach((step_index) => {

      let step = this.props.activity_plan.workout_steps[step_index];

      if (step.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED) {
        step.target_type = new_target_type;
        altered_steps.push(step);
      }
      else if (step.target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE) {
        step.target_type = new_target_type;
        altered_steps.push(step);
      }
      else if (step.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED_BY_ZONE) {
        step.target_type = new_target_type_zone;
      }
      else if (step.target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE_BY_ZONE) {
        step.target_type = new_target_type_zone;
      }

    });

    this.updateStructuredTrainingSteps(altered_steps);
    this.props.formChanged();

  }

  changeDurationType = (new_duration_type) => {
    this.duration_type = new_duration_type;
    let altered_steps = this.props.activity_plan.updateDurationType(new_duration_type, this.props.activity_plan.workout_steps);
    this.updateStructuredTrainingSteps(altered_steps);
    this.props.formChanged();
  }

  /**
   * Add the steps into the activity plan.
   */
  updateStructuredTrainingSteps = (steps, clear_previous_steps) => {
    this.props.activity_plan.updateStructuredTrainingSteps(steps, clear_previous_steps);
    store.dispatch(Action.receiveActivityPlans([this.props.activity_plan]));
  }

  handleMoveStep = (step_index_to_move, relative_step_index, before_or_after) => {

    if (step_index_to_move == relative_step_index) {
      // Don't do anything if the indexes match.
      return;
    }

    let step = this.props.activity_plan.getStepByIndex(step_index_to_move);
    step.markSectionHasChanges();

    if (before_or_after == 'before') {
      this.insertStepsBeforePosition([step], relative_step_index, true);
    }
    else {
      this.insertStepsAfterPosition([step], relative_step_index, true);
    }

  }

  handleMoveRepeatSection = (section_index_to_move, relative_section_index, before_or_after) => {

    let nested_steps_starting_order = this.props.activity_plan.getStepsBySection();
    let nested_steps_new_order = [];

    for (let i = 0; i < nested_steps_starting_order.length; i++) {

      if (i == section_index_to_move) {
        // This will be added relative to another section.
        continue;
      }
      else if (i == relative_section_index) {

        if (before_or_after == 'before') {
          nested_steps_new_order.push(nested_steps_starting_order[section_index_to_move]);
          nested_steps_new_order.push(nested_steps_starting_order[i]);
        }
        else {
          nested_steps_new_order.push(nested_steps_starting_order[i]);
          nested_steps_new_order.push(nested_steps_starting_order[section_index_to_move]);
        }

      }
      else {
        nested_steps_new_order.push(nested_steps_starting_order[i]);
      }

    }

    if (nested_steps_starting_order.length != nested_steps_new_order.length) {
      throw 'When trying to reorder sections, the new order contained fewer steps.';
    }

    // Flatten all the steps and refresh the indexes so they match the new
    // order of repeats.
    let steps = this.props.activity_plan.flattenNestedSteps(nested_steps_new_order);
    steps = this.props.activity_plan.refreshStepIndexes(steps);
    this.updateStructuredTrainingSteps(steps, true);
    this.props.formChanged({caller: 'handleMoveRepeatSection'});

  }

  handleSwitchRepeatSections = (repeat_step_index_a, repeat_step_index_b) => {
    let before_or_after = (repeat_step_index_a < repeat_step_index_b) ? 'after' : 'before';
    this.handleMoveRepeatSection(repeat_step_index_a, repeat_step_index_b, before_or_after);
  }

  handleMoveRepeatSectionUp = (repeat_step_index) => {
    this.handleSwitchRepeatSections(repeat_step_index, repeat_step_index - 1);
  }

  handleMoveRepeatSectionDown = (repeat_step_index) => {
    this.handleSwitchRepeatSections(repeat_step_index, repeat_step_index + 1);
  }

  insertStepsBeforePosition = (steps, position_index, remove_from_old_position) => {
    this.insertStepsAfterPosition(steps, position_index - 1, remove_from_old_position);
  }

  insertStepsAfterPosition = (steps_to_add, position_index, remove_from_old_position) => {

    const pushSteps = function (steps_to_add, steps_array) {
      for (let i = 0; i < steps_to_add.length; i++) {
        new_steps_array.push(steps_to_add[i]);
      }
      return new_steps_array;
    }

    let steps = this.props.activity_plan.getStepsAsSortedList();

    for (let step_index = 0; step_index < steps_to_add.length; step_index++) {

      let step = steps_to_add[step_index];

      if (remove_from_old_position) {
        for (var i = 0; i < steps.length; i++) {
          if (steps[i].index == step.index) {
            steps.splice(i, 1);
            continue;
          }
        }
      }

    }

    let new_steps_array = [];
    if (position_index == -1) {
      new_steps_array = pushSteps(steps_to_add, new_steps_array);
      new_steps_array = pushSteps(steps, new_steps_array);
    }
    else {
      for (let i = 0; i < steps.length; i++) {
        let step = steps[i];
        new_steps_array.push(step);
        if (position_index == step.index) {
          new_steps_array = pushSteps(steps_to_add, new_steps_array);
        }
      }
    }

    new_steps_array = this.props.activity_plan.refreshStepIndexes(new_steps_array);

    this.updateStructuredTrainingSteps(new_steps_array, true);
    this.props.formChanged({caller: 'insertStepsAfterPosition'});
  }

  handleAddNote = (insert_message_index) => {
    let step = new WorkoutStep();
    step.target_type = STEP_CONSTANTS.TARGET_TYPE_NOTE;
    step.duration_type = null;
    step.duration_value = null;
    step.note = '';
    step.markSectionHasChanges();
    this.insertStepsBeforePosition([step], insert_message_index);
  }

  handleAddRestStep = (insert_message_index) => {
    let step = new WorkoutStep();
    step.intensity = INTENSITY.REST;
    step.target_type = STEP_CONSTANTS.TARGET_TYPE_OPEN;
    step.duration_type = STEP_CONSTANTS.DURATION_TYPE_TIME
    step.markSectionHasChanges();
    this.insertStepsBeforePosition([step], insert_message_index);
  }

  handleAddStep = (section) => {
    let repeat_step_index = section.repeat_step_data.index;
    let step = new WorkoutStep();
    step.id = 'temp-' + getUniqueTemporaryID('workout-step');
    step.duration_type = (section.steps.length > 0) ? this.props.activity_plan.getDurationTypeFromSteps(section.steps) : this.getDurationType();
    step.target_type = this.getTargetType(repeat_step_index);
    step.intensity = INTENSITY.ACTIVE;
    step.markSectionHasChanges();
    this.insertStepsBeforePosition([step], repeat_step_index);
  }

  fetchTemplateSection = (section_id) => {
    return this.props.workout_sections[section_id];
  }

  addSectionToWorkout = (section_template, relative_section_index, before_or_after) => {

    const steps = this.props.activity_plan.getStepsAsSortedList();

    // Default to after.
    if (!before_or_after) {
      before_or_after = 'after';
    }

    // Default addding the section to the very end of the workout.
    let insert_position_index = (steps.length > 0) ? steps[steps.length - 1].index : 0;

    // If relative section index is set, find it the correct location to insert
    // it the new steps.
    if (!isNaN(relative_section_index)) {
      const nested_steps = this.props.activity_plan.getStepsBySection();
      for (let i = 0; i < nested_steps.length; i++) {
        if (i == relative_section_index) {
          if (before_or_after == 'after' || nested_steps[i].steps.length == 0) {
            // The last step in a section is the repeat step. So add relative to that.
            // If the section is empty of steps, then the first step would also be the
            // repeat step.
            insert_position_index = nested_steps[i].repeat_step_data.index;
          }
          else if (before_or_after == 'before') {
            // Add relative to the first step.
            insert_position_index = nested_steps[i].steps[0].index;
          }
        }
      }
    }

    let steps_to_insert = [];
    for (let i = 0; i < section_template.steps.length; i++) {
      let step = section_template.steps[i].duplicate();
      steps_to_insert.push(step);
    }

    if (before_or_after == 'before') {
      this.insertStepsBeforePosition(steps_to_insert, insert_position_index);
    }
    else {
      this.insertStepsAfterPosition(steps_to_insert, insert_position_index);
    }

  }

  handleAddSectionToTemplatesButtonPressed = (section) => {

    let template_section = {
      id: null,
      title: '',
      discipline: this.props.activity_plan.discipline,
      steps: section.steps.slice(),
    };

    template_section.steps.push(section.repeat_step_data);

    this.setState({
      active_template_section: template_section,
    });

    this.closeSectionMenu();

  }

  handleSwitchDurationType = (section) => {
    let current_duration_type = this.props.activity_plan.getDurationTypeFromSteps(section.steps);
    let new_duration_type = (current_duration_type == STEP_CONSTANTS.DURATION_TYPE_TIME) ? STEP_CONSTANTS.DURATION_TYPE_DISTANCE : STEP_CONSTANTS.DURATION_TYPE_TIME;
    let altered_steps = this.props.activity_plan.updateDurationType(new_duration_type, section.steps);
    this.updateStructuredTrainingSteps(altered_steps);
    this.props.formChanged({caller: 'switchDurationType'});
    this.closeSectionMenu();
  }

  handleDuplicateSection = (section) => {

    let last_position_index = section.repeat_step_data.index;
    let duplicated_steps = [];
    for (let i = 0; i < section.steps.length; i++) {
      let duplicated_step = section.steps[i].duplicate();
      last_position_index = section.steps[i].index;
      duplicated_steps.push(duplicated_step);
    }
    this.insertStepsAfterPosition(duplicated_steps, last_position_index, false);

    let duplicated_repeat_step = section.repeat_step_data.duplicate();
    duplicated_repeat_step.duration_value = duplicated_steps[0].index;

    this.insertStepsAfterPosition([duplicated_repeat_step], last_position_index, false);

    this.closeSectionMenu();
  }

  handleAddRepeatStep = (first_step_index_to_repeat, last_step_index_to_repeat) => {

    var steps = this.props.activity_plan.getStepsAsSortedList();

    let step = new WorkoutStep();
    step.target_type = 2; // repeat
    step.duration_type = STEP_CONSTANTS.DURATION_TYPE_REPEAT_QUANTITY;
    step.duration_value = first_step_index_to_repeat;
    step.target_value = 1; // Repeat once to start with.

    this.insertStepsAfterPosition([step], last_step_index_to_repeat);
  }


  handleRemoveSectionClicked = (section) => {
    activateConfirmationBox({
      message: 'Are you sure you want to delete this section?',
      confirm_callback: (closeConfirmationCallback, section) => {
        this.removeSection(section);
        closeConfirmationCallback();
      },
      confirm_callback_data: section,
    });

    this.closeSectionMenu();
  }

  removeSection = (section) => {

    // Collect a list of step uuids to remove.
    let step_uuids_to_remove = [];
    for (let i = 0; i < section.steps.length; i++) {
      step_uuids_to_remove.push(section.steps[i].uuid);
    }
    step_uuids_to_remove.push(section.repeat_step_data.uuid);

    // Go through all the steps looking for these uuids.
    let steps = this.props.activity_plan.getStepsAsSortedList();
    let new_steps_array = [];
    for (let i = 0; i < steps.length; i++) {

      if (step_uuids_to_remove.indexOf(steps[i].uuid) > -1) {

        // If the repeat step exists in the database, flag it as something to
        // delete.
        if (!isNaN(steps[i].id)) {
          this.props.activity_plan.deleted_workout_steps.push(steps[i].id);
        }

      }
      else {
        new_steps_array.push(steps[i]);
      }

    }

    new_steps_array = this.props.activity_plan.refreshStepIndexes(new_steps_array);
    this.updateStructuredTrainingSteps(new_steps_array, true);
    this.props.formChanged({caller: 'removeSection'});

  }

  removeStep = (step_index) => {

    let steps = this.props.activity_plan.getStepsAsSortedList();

    // If the step exists in the database, flag it as something to
    // delete.
    if (!isNaN(steps[step_index].id)) {
      this.props.activity_plan.deleted_workout_steps.push(steps[step_index].id);
    }

    // Remove the step from the array.
    steps.splice(step_index, 1);

    steps = this.props.activity_plan.refreshStepIndexes(steps);
    this.updateStructuredTrainingSteps(steps, true);

    this.props.formChanged({caller: 'removeStep'});
  }

  handleChangeRepeatSectionName = (repeat_step_data, event) => {
    repeat_step_data.name = event.target.value;
    repeat_step_data.markSectionHasChanges();
    this.updateStructuredTrainingSteps([repeat_step_data]);
    this.props.formChanged({caller: 'handleChangeRepeatSectionName'});
  }

  handleRepeatDecrease = (repeat_step_data, event) => {
    if (repeat_step_data.target_value == 1) {
      return;
    }
    else {
      repeat_step_data.target_value = repeat_step_data.target_value - 1;
      repeat_step_data.markSectionHasChanges();
      this.updateStructuredTrainingSteps([repeat_step_data]);
      this.props.formChanged({caller: 'handleRepeatDecrease'});
    }
  }

  handleRepeatIncrease = (repeat_step_data, event) => {
    repeat_step_data.target_value = repeat_step_data.target_value + 1;
    repeat_step_data.markSectionHasChanges();
    this.updateStructuredTrainingSteps([repeat_step_data]);
    this.props.formChanged({caller: 'handleRepeatIncrease'});
  }

  handleCorosButtonClick = () => {
    handleCorosButtonClick();
  }

  handleGarminButtonClick = () => {
    handleGarminButtonClick(
      this.props.activity_plan.id,
      this.props.training_session.id,
      this.props.athlete_data.id
    );
  }

  handleOpenSectionMenuClicked = (repeat_step_data, event) => {

    if (this.state.open_section_menu == repeat_step_data.uuid) {
      this.closeSectionMenu();
    }
    else {
      this.setState({open_section_menu: repeat_step_data.uuid});
    }

    // When clicking outside of the menu, close it.
    document.addEventListener('click', this.handleDocumentPress);
    document.addEventListener('touchstart', this.handleDocumentPress);
  }

  closeSectionMenu = () => {
    this.setState({open_section_menu: null});
    document.removeEventListener('click', this.handleDocumentPress);
    document.removeEventListener('touchstart', this.handleDocumentPress);
  }

  handleDocumentPress = (event) => {
    if (!event.target.classList.contains('dropdown_menu__button')) {
      this.closeSectionMenu();
    }
  }

  handleCloseStructuredWorkout = () => {
    this.props.closeOverlay();
  }

  getScrollPosition = () => {
    if (this.props.perspective == 'athlete') {
      // TODO This won't work in mobile mode as it scrolls on a different element.
      // aas the page scrolls the menu won't scroll with the item but will stay
      // fixed in position on the page.
      return document.querySelector('.page-athlete-calendar__inner').scrollTop;
    }
    else if (this.workoutScrollableSection.current) {
      return this.workoutScrollableSection.current.scrollTop;
    }
  }

  setActiveTemplateSection = (template_section) => {
    this.setState({active_template_section: template_section});
  }

  handleOpenZoneSettings = () => {
    window.open(this.props.athlete_data.getEditZonesPath(this.props.perspective), '_blank');
  }

  render() {

    let nested_steps = this.props.activity_plan.getStepsBySection();
    let step_sections = [];

    let distance_unit_preference;
    if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.SWIMMING) {
      // NOTE: Not yet supporting yards.
      //distance_unit_preference = this.props.training_session.getPoolUnit();
      distance_unit_preference = 'meters';
    }
    else {
      distance_unit_preference = this.props.training_session.getDistanceUnit();
    }

    for (var i = 0; i < nested_steps.length; i++) {

      let repeat_section_is_active = false;

      var rendered_steps = [];
      for (var j = 0; j < nested_steps[i].steps.length; j++) {

        var step = nested_steps[i].steps[j];

        var prev_step = null;
        if (j > 0) {
          prev_step = nested_steps[i].steps[j-1];
        }

        var next_step = null;
        if (j < nested_steps[i].steps.length - 1) {
          next_step = nested_steps[i].steps[j+1];
        }

        let step_is_active = (this.state.active_step_id == step.id) ? true : false;

        if (step_is_active) {
          repeat_section_is_active = true;
        }

        let custom_zones = [];
        for (let i = 0; i < this.props.custom_zones.length; i++) {
          if (this.props.custom_zones[i].discipline_string == this.props.activity_plan.getDisciplineString()) {
            custom_zones.push(this.props.custom_zones[i]);
          }
        }

        rendered_steps.push(
          <StructuredTrainingStepEditor
            key={step.index}
            index={step.index}
            step={step}
            prev_step={prev_step}
            next_step={next_step}
            repeat_step_data={nested_steps[i].repeat_step_data}
            updateStructuredTrainingSteps={this.updateStructuredTrainingSteps}
            removeStep={this.removeStep}
            step_is_active={step_is_active}
            athlete_data={this.props.athlete_data}
            custom_zones={custom_zones}
            distance_unit_preference={distance_unit_preference}
            discipline={this.props.activity_plan.discipline}
            target_type_category={this.state.target_type_category}
            handleMoveRepeatUp={this.handleMoveRepeatUp}
            handleMoveRepeatDown={this.handleMoveRepeatDown}
            formChanged={this.props.formChanged}
            setActiveStep={this.setActiveStep}
            getScrollPosition={this.getScrollPosition}
            handleOpenZoneSettings={this.handleOpenZoneSettings}
            saveWorkoutSectionsOrder={this.saveWorkoutSectionsOrder}
          />
        );

      }

      let remove_section_menu_item;
      // Show option to remove section only if there will still be a section
      // left afterwards. At the moment, one section always needs to be there
      // so this button wouldn't work for the last remaining section.
      if (nested_steps.length > 1) {
        remove_section_menu_item = <div key="remove-section" className="dropdown_menu__menu-item dropdown_menu__menu-item--clickable dropdown_menu__menu-item__remove-section" onClick={this.handleRemoveSectionClicked.bind(null, nested_steps[i])}>remove section</div>;
      }
      else {
        remove_section_menu_item = <div key="remove-section" className="dropdown_menu__menu-item dropdown_menu__menu-item--disabled dropdown_menu__menu-item__remove-section">cannot remove last section</div>;
      }

      let duplicate_section_menu_item = <div className="dropdown_menu__menu-item dropdown_menu__menu-item--clickable dropdown_menu__menu-item__duplicate-section" onClick={this.handleDuplicateSection.bind(null, nested_steps[i])}>duplicate section</div>;

      let switch_duration_type_menu_item;
      if (hasBetaAccess('structured_training_variable_sections_durations')) {
        const switch_duration_type_menu_item_text = (this.props.activity_plan.getDurationTypeFromSteps(nested_steps[i].steps) == STEP_CONSTANTS.DURATION_TYPE_DISTANCE) ? 'set section by time' : 'set section by distance';
        switch_duration_type_menu_item = <div className="dropdown_menu__menu-item dropdown_menu__menu-item--clickable dropdown_menu__menu-item__switch-section-duration-type" onClick={this.handleSwitchDurationType.bind(null, nested_steps[i])}>{switch_duration_type_menu_item_text}</div>;
      }

      let add_to_templates_menu_item;
      if (hasBetaAccess('structured_training_section_templates')) {
        add_to_templates_menu_item = <div className="dropdown_menu__menu-item dropdown_menu__menu-item--clickable dropdown_menu__menu-item__add-section-to-templates" onClick={this.handleAddSectionToTemplatesButtonPressed.bind(null, nested_steps[i])}>add section to templates</div>;
      }

      let repeat_section_content = null;
      let up_button;
      let down_button;
      if (nested_steps[i].repeat_step_data != null) {

        if (i == 0) {
          up_button = <span className="icon up-arrow-icon icon_disabled move-section-up-button">up</span>
        }
        else {
          up_button = <span className="icon up-arrow-icon icon_active move-section-up-button" onClick={this.handleMoveRepeatSectionUp.bind(null, i)}>up</span>
        }

        if (i == nested_steps.length - 1) {
          down_button = <span className="icon down-arrow-icon icon_disabled move-section-down-button">down</span>
        }
        else {
          down_button = <span className="icon down-arrow-icon icon_active move-section-down-button" onClick={this.handleMoveRepeatSectionDown.bind(null, i)}>down</span>
        }

        let section_name = <AutosizeInput
          style={{marginBottom: '4px', fontWeight: 'bold'}}
          type="text"
          placeholder={'Untitled section'}
          className="structured_training__section-title session-form__text-field"
          maxLength="20"
          onChange={this.handleChangeRepeatSectionName.bind(null, nested_steps[i].repeat_step_data)}
          value={nested_steps[i].repeat_step_data.name || ''}
        />

        const drag_handle_styles = {
          width: '12px',
          height: '19px',
          display: 'block',
          borderRadius: '2px',
          cursor: 'move',
          margin: '0px 3px 21px 0px',
          float: 'left',
        }

        const menu_style = {
          background: 'white',
          borderRadius: '5px',
          border: '1px solid silver',
        }

        let section_menu;
        if (this.state.open_section_menu == nested_steps[i].repeat_step_data.uuid) {
          section_menu = (
            <div
             className="step-section__menu dropdown_menu__body"
             style={menu_style}
             onBlur={this.handleSectionMenuBlur}
             tabIndex="0"
            >
              {duplicate_section_menu_item}
              {remove_section_menu_item}
              {add_to_templates_menu_item}
              {switch_duration_type_menu_item}
            </div>
          );
        }

        repeat_section_content = (
          <div style={{marginTop: '2px'}}>
            <div className="step-section__drag-handle" style={drag_handle_styles}></div>
            <div>{section_name} <span className="small-buttons-group move-section-buttons">{up_button}{down_button}</span></div>
            <div style={{position: 'relative'}}>
              <span style={{fontStyle: 'italic', fontSize: '0.9em'}}>repeat x {nested_steps[i].repeat_step_data.target_value}</span>
              {' '}
              <span className="small-buttons-group">
                <span className="small-button small-button--dark reduce-repeat-button" onClick={this.handleRepeatDecrease.bind(null, nested_steps[i].repeat_step_data)}>-</span>
                <span className="small-button small-button--dark increase-repeat-button" onClick={this.handleRepeatIncrease.bind(null, nested_steps[i].repeat_step_data)}>+</span>
              </span>
              {' '}
              <div style={{display: 'inline-block'}}>
                <span key="open-section-menu" className="dropdown_menu__button small-button small-button--dark" onClick={this.handleOpenSectionMenuClicked.bind(null, nested_steps[i].repeat_step_data)}>section options</span>
                {section_menu}
              </div>
            </div>
          </div>
        );

      }

      let add_step_button = <span className="small-button small-button--dark add-step-button" onClick={this.handleAddStep.bind(null, nested_steps[i])}>add step</span>;
      let add_note_button = <span className="small-button small-button--dark add-note-button" onClick={this.handleAddNote.bind(null, nested_steps[i].repeat_step_data.index)}>add note</span>;
      let add_rest_button;
      if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.SWIMMING) {
        add_rest_button = <span className="small-button small-button--dark add-rest-button" onClick={this.handleAddRestStep.bind(null, nested_steps[i].repeat_step_data.index)}>add rest</span>;
      }

      let background_colour = (repeat_section_is_active) ? '#f6f9ff' : 'white';

      let total_distance;
      let total_time;
      if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.SWIMMING) {
        total_distance = <span className="section-distance">{decimalPlaces(nested_steps[i].getTotalDistance('meters'), 0, 'zero')}m</span>;

        const total_time_seconds = nested_steps[i].getTotalTime();
        if (total_time_seconds) {
          total_time = <span className="section-distance">{formatTime(total_time_seconds)}</span>;
        }

      }

      step_sections.push(
        <div className={"step-section step-section-" + i} data-section-index={i} key={i} style={{padding: '8px', background: background_colour, marginBottom: '1px'}}>
          <div className="step-section__repeat_data" data-section-repeat-step-index={nested_steps[i].repeat_step_data.index} style={{}}>{repeat_section_content}</div>
          <div className="step-section__steps" style={{margin: '5px 0'}}>{rendered_steps}</div>
          <div className="small-buttons-group" style={{margin: '4px 0 0 15px'}}>
            {add_step_button}{add_rest_button}{add_note_button} {total_distance} {total_time}
          </div>
        </div>
      );

    }

    let graph = <Graph
      context="editor"
      duration_type={this.getDurationType()}
      activity_or_section="activity"
      activity_or_section_id={this.props.activity_plan.id || 'new'}
      discipline={this.props.activity_plan.discipline}
      distance_unit_preference={this.props.training_session.getDistanceUnit()}
      nested_steps={nested_steps}
      athlete_data={this.props.athlete_data}
      active_step_id={this.state.active_step_id}
      setActiveStep={this.setActiveStep}
      height="180px"
    />

    let last_index = (nested_steps.length > 0) ? nested_steps[nested_steps.length - 1].repeat_step_data.index : 0;
    let new_repeatable_section_button = (
      <div style={{margin: '8px', display: 'inline-block'}} className='small-button small-button--dark structured_training__add-section' onClick={this.handleAddRepeatStep.bind(null, null, last_index)}>add section</div>
    );

    let close_button = <div style={{textAlign: 'center', width: '100%', clear: 'both'}}><span className="small-button structured-workout-editor__close-button" onClick={this.handleCloseStructuredWorkout}>close</span></div>

    let download_buttons;
    if (!isNaN(this.props.activity_plan.id) && this.props.athlete_data) {

      let mrc_button, zwo_button, erg_button, fit_button, coros_button, garmin_button;

      const mrc_export_status = this.props.activity_plan.checkExportStatusAsMRC(
        this.props.training_session,
        this.props.athlete_data?.bike_ftp,
        this.props.athlete_data?.training_zones,
        this.props.activity_plan.discipline
      );

      const zwo_export_status = this.props.activity_plan.checkExportStatusAsZWO(
        this.props.training_session,
        this.props.athlete_data?.bike_ftp,
        this.props.athlete_data?.training_zones,
        this.props.activity_plan.discipline
      );

      const erg_export_status = this.props.activity_plan.checkExportStatusAsERG(
        this.props.training_session,
        this.props.athlete_data?.bike_ftp,
        this.props.athlete_data?.training_zones,
        this.props.activity_plan.discipline
      );

      const fit_export_status = this.props.activity_plan.checkExportStatusAsFIT(
        this.props.training_session,
        this.props.athlete_data?.bike_ftp,
        this.props.athlete_data?.training_zones,
        this.props.activity_plan.discipline
      );

      if (mrc_export_status.can_export) {
        mrc_button = <a className="small-button" href={this.props.activity_plan.mrcDownloadURL(this.props.training_session.id)} target="_blank">mrc</a>;
      } else {
        mrc_button = <span className="small-button small-button_disabled" title={mrc_export_status.description}>mrc</span>;
      }

      if (zwo_export_status.can_export) {
        zwo_button = <a className="small-button" href={this.props.activity_plan.zwoDownloadURL(this.props.training_session.id)} target="_blank">zwo</a>;
      } else {
        zwo_button = <span className="small-button small-button_disabled" title={zwo_export_status.description}>zwo</span>;
      }

      if (erg_export_status.can_export) {
        erg_button = <a className="small-button" href={this.props.activity_plan.ergDownloadURL(this.props.training_session.id)} target="_blank">erg</a>;
      } else {
        erg_button = <span className="small-button small-button_disabled" title={erg_export_status.description}>erg</span>;
      }

      if (fit_export_status.can_export) {
        fit_button = <a className="small-button" href={this.props.activity_plan.fitDownloadURL(this.props.training_session.id)} target="_blank">fit</a>;
      } else {
        fit_button = <span className="small-button small-button_disabled" title={fit_export_status.description}>fit</span>;
      }

      if (this.props.athlete_data.coros_connection) {
        coros_button = <span className="small-button" onClick={this.handleCorosButtonClick}>coros</span>
      }

      if (this.props.athlete_data.garmin_connection) {
        garmin_button = <span className="small-button" onClick={this.handleGarminButtonClick}>garmin</span>
      }

      download_buttons = (
        <div className="buttons" style={{float: 'right'}}>
          <span style={{fontSize: '11px'}}>download workout</span>&nbsp;
          <span className="small-buttons-group">{garmin_button}{coros_button}{fit_button}{zwo_button}{erg_button}{mrc_button}</span> <a className="small-button" href="#help=structured-workouts">?</a>
        </div>
      );

    }

    let duration_text;
    if (this.getDurationType() == STEP_CONSTANTS.DURATION_TYPE_TIME) {
      duration_text = 'time';
    }
    else {
      if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.SWIMMING) {
        duration_text = 'meters';
      }
      else {
        duration_text = 'km';
      }
    }

    let discipline_text = '';
    if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.CYCLING) {
      discipline_text = 'Bike';
    }
    else if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.RUNNING) {
      discipline_text = 'Run';
    }
    else if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.SWIMMING) {
      discipline_text = 'Swim';
    }
    else {
      throw("Invalid discipline");
    }

    const discipline_switcher = <span onClick={this.toggleDisciplineType} style={{textDecoration: 'underline', cursor: 'pointer'}}>{discipline_text}</span>;

    let duration_switcher;
    if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.RUNNING || this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.CYCLING) {
      duration_switcher = <span onClick={this.toggleDurationType} style={{textDecoration: 'underline', cursor: 'pointer'}}>{duration_text}</span>;
    }
    else if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.SWIMMING) {
      // Garmin does not support swimming by time. COROS does, but it may be best to wait
      // and see if anybody asks for that and in the meantime not support it.
      duration_switcher = <span>{duration_text}</span>;
    }

    let target_switcher;
    if (this.props.activity_plan.discipline == WORKOUT_DISCIPLINES.RUNNING) {
      target_switcher = <span> and <span onClick={this.toggleTargetTypeCategory} style={{textDecoration: 'underline', cursor: 'pointer'}}>{this.state.target_type_category}</span></span>;
    }

    let workout_overview = (
      <div className="structured-workout__description-editor-title"><p
        style={{padding: '5px 8px', background: 'white', margin: '0 0 1px'}}>
          {discipline_switcher} workout. Set steps based on {duration_switcher}{target_switcher}.
      </p></div>
    );

    let save_section_template_popup;
    if (this.state.active_template_section) {
      save_section_template_popup = <TemplateSectionPopup
        active_template_section={this.state.active_template_section}
        duration_type={this.props.activity_plan.getDurationTypeFromSteps(this.state.active_template_section.steps)}
        discipline={this.props.activity_plan.discipline}
        athlete_data={this.props.athlete_data}
        distance_unit_preference={this.props.training_session.getDistanceUnit()}
        setWorkoutSections={this.props.setWorkoutSections}
        setActiveTemplateSection={this.setActiveTemplateSection}
        addSectionToWorkout={this.addSectionToWorkout}
      />
    }

    // TODO Add a note that they are overriding a session first set on such and such a date.

    return (
      <div className={"structured-workout__editor structured-workout__editor--" + this.props.perspective + " session-form--intensity-" + this.props.training_session.intensity} style={{height: '100%'}}>
        <div className="structured-workout__description-editor" ref={this.workoutScrollableSection} style={{float: 'left', minHeight: '100%', width: '378px', padding: '0', borderRight: '1px solid #515151', overflow: 'auto', boxSizing: 'border-box', background: '#ffffff', display: 'flex', flexDirection: 'column'}}>
          <div style={{background: 'rgb(63, 63, 63)', flex: '1'}}>
            {workout_overview}
            <div className="step-sections-container">{step_sections}</div>
            {new_repeatable_section_button}
          </div>
        </div>
        <div style={{float: 'right', width: 'calc(100% - 378px)', padding: '8px', boxSizing: 'border-box', height: '100%'}}>
          <div style={{height: 'calc(100% - 20px)'}}>
            {download_buttons}
            <h3 style={{marginTop: '0px'}}>Structured workout</h3>
            {graph}
            <StructuredWorkoutEditorSectionTemplates
              workout_sections_loaded={this.props.workout_sections_loaded}
              workout_sections={this.props.workout_sections}
              activity_discipline={this.props.activity_plan.discipline}
              setActiveTemplateSection={this.setActiveTemplateSection}
              setWorkoutSections={this.props.setWorkoutSections}
              saveWorkoutSectionsOrder={this.props.saveWorkoutSectionsOrder}
            />
            {save_section_template_popup}
          </div>
          {close_button}
        </div>
      </div>
    );

  }
}

class TemplateSectionPopup extends React.Component {

  handleAddSectionToTemplates = () => {
    ajaxRequest({
      url: '/coach/workout-section/save/',
      method: 'POST',
      data: this.props.active_template_section,
      success: this.sectionTemplateSaved,
      expected_response_type: 'json',
    });
  }

  handleClosePopup = () => {
    this.props.setActiveTemplateSection(null);
  }

  sectionTemplateSaved = (response, status, request_parameters) => {
    this.props.setWorkoutSections([response.workout_section]);
    this.props.setActiveTemplateSection(null);
  }

  handleChangeNewSectionTitle = (event) => {
    this.props.active_template_section.title = event.target.value;
    this.props.setActiveTemplateSection(this.props.active_template_section);
  }

  handleAddSectionToWorkout = (event) => {
    this.props.addSectionToWorkout(this.props.active_template_section);
    this.props.setActiveTemplateSection(null);
  }

  handleDeleteSectionTemplate = (event) => {
    activateConfirmationBox({
      message: 'Are you sure you want to delete this section template?',
      confirm_callback: (closeConfirmationCallback) => {
        ajaxRequest({
          url: '/coach/workout-section/delete/',
          method: 'POST',
          data: this.props.active_template_section,
          success: (response, status, request_parameters) => {
            this.props.setWorkoutSections([response.workout_section]);
            this.props.setActiveTemplateSection(null);
            closeConfirmationCallback();
          },
          expected_response_type: 'json',
        });
      }
    });
  }

  render () {

    const popup_container_css = {
      position: 'fixed',
      top: 0,
      bottom: 0,
      right: 0,
      left: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      zIndex: 200
    }

    const popup_inner_css = {
      background: 'white',
      width: 500,
      margin: '90px auto 0',
      top: 50,
    }

    let nested_steps = organiseSortedStepsAsNestedSteps(this.props.active_template_section.steps);

    let section_summary = (
      <StructuredTrainingSectionDisplay
        section={nested_steps[0]}
        discipline={this.props.discipline}
        athlete_data={this.props.athlete_data}
        handleOpenZoneSettings={this.props.handleOpenZoneSettings}
        distance_unit_preference={this.props.distance_unit_preference}
      />
    );

    let graph = <Graph
      context="preview"
      duration_type={this.props.duration_type}
      activity_or_section="section"
      activity_or_section_id={this.props.active_template_section.id || 'new'}
      discipline={this.props.discipline}
      distance_unit_preference={this.props.distance_unit_preference}
      nested_steps={nested_steps}
      athlete_data={this.props.athlete_data}
      height="140px"
    />

    let section_template_popup;
    if (this.props.active_template_section.id) {

      // Already saved, so the popup is for previewing, deleting, etc.
      section_template_popup = (
        <div style={popup_inner_css}>
          <div style={{padding: '8px', textAlign: 'center'}}>
            <h3 style={{margin: '8px 0px 6px'}}>{this.props.active_template_section.title}</h3>
            <div className="small-button" style={{}} onClick={this.handleDeleteSectionTemplate}>Delete section template</div>
          </div>
          <div style={{borderWidth: '1px 0px 1px', borderColor: 'black', borderStyle: 'solid', marginBottom: '15px', padding: '8px'}}>
            {section_summary}
            {graph}
          </div>
          <div style={{textAlign: 'center', width: '100%', clear: 'both'}}>
            <div className="small-button small-button--dark" style={{margin: '15px 0'}} onClick={this.handleAddSectionToWorkout}>Add section to workout</div>
          </div>
          <div style={{textAlign: 'center', width: '100%', clear: 'both', padding: '8px 0 15px'}}>
            <span className="small-button section-template-popup__close-button" onClick={this.handleClosePopup}>close</span>
          </div>
        </div>
      );

    }
    else {

      // Not yet saved, so the popup is for creating a brand new section.
      const button_text = 'Save';
      const button_disabled = (this.props.active_template_section.title.length > 0) ? false : true;
      section_template_popup = (
        <div style={popup_inner_css}>
          <div style={{padding: '8px', textAlign: 'center'}}>
            <h3>Save this section as a template</h3>
          </div>
          <div style={{borderWidth: '1px 0px 1px', borderColor: 'black', borderStyle: 'solid', marginBottom: '15px', padding: '8px'}}>
            {section_summary}
            {graph}
          </div>
          <div style={{padding: '8px', textAlign: 'center'}}>
            <label htmlFor="new-section-template-title" style={{fontWeight: 'bold', fontSize: '1.1em'}}>Section template name</label>
            <div style={{margin: '4px 0 15px'}}>Please give this section a title so you can recognise it later.</div>
            <input id="new-section-template-title" type="text" style={{width: '10em'}}
              autoFocus
              value={this.props.active_template_section.title}
              onChange={this.handleChangeNewSectionTitle}
            />
            <input
              id="save-new-section-template"
              name="save-new-section-template"
              type="submit"
              className="standard-button"
              value={button_text}
              disabled={button_disabled}
              onClick={this.handleAddSectionToTemplates}
              style={{display: 'block'}}
            />
          </div>
          <div style={{textAlign: 'center', width: '100%', clear: 'both', padding: '8px 0 15px'}}>
            <span className="small-button section-template-popup__close-button" onClick={this.handleClosePopup}>cancel</span>
          </div>
        </div>
      );
    }

    return (
      <div className="section-template-popup__container" style={popup_container_css}>
        {section_template_popup}
      </div>
    );

  }
}

class StructuredWorkoutEditorSectionTemplates extends React.Component {

  TEMPLATE_MARKER_CSS_SELECTOR = '.structured-workout__section-template-marker';

  componentDidMount = () => {
    this.addDragTemplateSectionBehaviour();
    this.addDropTemplateSectionBehaviour();
  }

  componentWillUnmount = () => {
    interact(this.TEMPLATE_MARKER_CSS_SELECTOR).unset();
  }

  handleTemplateClick = (template_section_id) => {
    this.props.setActiveTemplateSection(this.props.workout_sections[template_section_id]);
  }

  addDropTemplateSectionBehaviour = () => {

    let self = this;

    // Add a drop marker to the DOM.
    self.section_droppable_spacer = document.createElement('div');
    self.section_droppable_spacer.classList.add('droppable-section-spacer');
    self.section_droppable_spacer.style.width = '5px';
    self.section_droppable_spacer.style.height = '22px';
    self.section_droppable_spacer.style.background = '#99e5f3';
    self.section_droppable_spacer.style.display = 'inline-block';
    self.section_droppable_spacer.style.margin= '4px 4px 4px 0px';
    self.section_droppable_spacer.style.verticalAlign = 'bottom';
    self.section_droppable_spacer.style.borderRadius = '3px';

    if (!interact) {
      return;
    }

    interact(self.TEMPLATE_MARKER_CSS_SELECTOR)
      .dropzone({

        ondragenter: function (event) {
          event.target.appendChild(self.section_droppable_spacer);
        },

        ondropmove: function (event) {

          // Determine if the insert section marker should show before or after
          // this section template item.
          const x_cursor_position = event.dragEvent.clientX;
          const bounding_client_rect = event.target.getBoundingClientRect();
          const drop_element_x_midpoint = bounding_client_rect.x + (bounding_client_rect.width / 2);
          const before_or_after = (x_cursor_position < drop_element_x_midpoint) ? 'before' : 'after';

          // Add the insert marker in the correct place.
          if (before_or_after == 'before') {
            event.target.parentNode.insertBefore(self.section_droppable_spacer, event.target);
          }
          else {
            event.target.parentNode.insertBefore(self.section_droppable_spacer, event.target.nextSibling);
          }

          event.interaction.section_final_position = {
            dropped_section_id: parseInt(event.target.getAttribute('data-section_id')),
            relative_position: before_or_after,
          };

        },

        ondragleave: function (event) {
          self.section_droppable_spacer.remove();
        },

        ondropdeactivate: function (event) {
          self.section_droppable_spacer.remove();
        },

        ondrop: function (event) {
          const dragged_section_id = event.dragEvent.currentTarget.getAttribute('data-section_id');

          // So now I need use this data to organise the data.
          self.reorderTemplateSections(
            dragged_section_id,
            event.interaction.section_final_position.relative_position,
            event.interaction.section_final_position.dropped_section_id
          );

        }

      })

  }

  reorderTemplateSections = (section_to_move, before_or_after, relative_section_id) => {

    // Then I need to do the reordering.

    let ordered_workout_sections = Object.values(this.props.workout_sections);
    ordered_workout_sections.sort(function (a, b) { return a.order - b.order});

    let new_order = [];
    ordered_workout_sections.forEach((workout_section) => {

      if (workout_section.id == section_to_move) {
        return;
      }
      else if (workout_section.id == relative_section_id) {
        if (before_or_after == 'before') {
          new_order.push(parseInt(section_to_move))
          new_order.push(workout_section.id);
        }
        else {
          new_order.push(workout_section.id);
          new_order.push(parseInt(section_to_move))
        }
      }
      else {
        new_order.push(workout_section.id);
      }

    });

    if (new_order.length != ordered_workout_sections.length) {
      throw 'Mismatched lengths of section orders when sorting.';
    }

    let updated_sections = [];
    let found_first_section_to_change = false;
    let first_section_order = null;
    for (let i = 0; i < new_order.length; i++) {
      let section_id = new_order[i];
      let section = this.props.workout_sections[section_id];

      if (!found_first_section_to_change && (section_id == section_to_move || section_id == relative_section_id)) {
        found_first_section_to_change = true;
        first_section_order = (section.order) ? section.order : 1;
      }

      if (found_first_section_to_change) {
        section.order = first_section_order + i;
        updated_sections.push(section);
      }

    }

    // Update any items that have changed.
    this.props.setWorkoutSections(updated_sections);
    this.props.saveWorkoutSectionsOrder(updated_sections);

  }

  addDragTemplateSectionBehaviour = () => {

    let self = this;

    interact(self.TEMPLATE_MARKER_CSS_SELECTOR)
      .draggable({
        manualStart: true,
        onmove: dragMoveListener,
        onend: dragEndListener,
      })
      .on('tap', (event) => {
        const section_id = event.target.getAttribute('data-section_id');
        self.handleTemplateClick(section_id)
      })
      .on('move', (event) => {

        // Start the drag operation instantly when interacting with a mouse.
        // The check for the correct mouse button stops a right contextual
        // click from accidentally starting the drag operation. The check
        // for undefined makes sure this works in Safari version 10.
        if (event.pointerType == 'mouse' && event.interaction.pointerIsDown && !event.interaction.interacting() && (typeof event.buttons == 'undefined' || event.buttons == 1)) {

          // Start interaction targeting the section box.
          event.interaction.start({ name: 'drag' }, event.interactable, event.target);

        }

      });
  }

  render () {

    if (!this.props.workout_sections_loaded || !hasBetaAccess('structured_training_section_templates')) {
      return null;
    }

    let ordered_workout_sections = Object.values(this.props.workout_sections);
    ordered_workout_sections.sort(function (a, b) { return a.order - b.order});

    let workout_sections = [];
    ordered_workout_sections.forEach((workout_section) => {
      if (workout_section.discipline == this.props.activity_discipline) {
        workout_sections.push(
          <div
            className="structured-workout__section-template-marker tag"
            data-section_id={workout_section.id}
            key={workout_section.id}
            style={{margin: '4px 4px 4px 0px', padding: '4px'}}
          >
            {workout_section.title}
          </div>
        );
      }
    });

    return (
      <div className="structured-workout__section-templates">
        <h4>Template Workout Sections</h4>
        <p>Click on the 'add to templates' buttons on the left to turn sections of your workout into templates that you can use when building other workouts. You can drag and drop these into a workout or click on the templates to preview what they look like.</p>
        {workout_sections}
      </div>
    );
  }

}

function isDropdownOpenEvent(event){

  if (event.type === 'click'){
    return true;
  }

  else if (event.type === 'keydown'){

    let key = event.key || event.keyCode;

    switch (key) {
      case 'Enter':
      case 13:
      case ' ':
      case 32:
      case 'ArrowDown':
      case 40:
      case 'ArrowUp':
      case 38:
        return true
    }

  }

  return false;
}

class DropDownWidget extends React.Component {

  constructor(props) {
    super(props);

    this.dropdownHead = React.createRef();

    this.state = {
      dropdown_open: false,
      menu_item_selected: false,
    };

  }

  handleToggleDropdown = (event) => {
    if (isDropdownOpenEvent(event) === true) {
      this.setState({dropdown_open: !this.state.dropdown_open, menu_item_selected: false});
    }
  }

  handleCloseDropdown = (menu_item_selected) => {
    this.setState({dropdown_open: false, menu_item_selected: menu_item_selected});
  }

  handleDropDownWidgetFocus = (event) => {
    this.setState({hovering: true});
  }

  handleDropDownWidgetBlur = (event) => {
    this.setState({hovering: false});
  }

  handleDropDownWidgetMouseEnter = (event) => {
    this.setState({hovering: true});
  }

  handleDropDownWidgetMouseLeave = (event) => {
    this.setState({hovering: false});
  }

  focusDropdownHead = () => {
    this.dropdownHead.current.focus();
  }

}

class EditStepPopupWidget extends DropDownWidget {

  render() {

    let dropdown_list;
    if (this.state.dropdown_open) {
      dropdown_list = <EditStepPopupDropDown
        discipline={this.props.discipline}
        step={this.props.step}
        updateStructuredTrainingSteps={this.props.updateStructuredTrainingSteps}
        handleCloseDropdown={this.handleCloseDropdown}
        formChanged={this.props.formChanged}
        getScrollPosition={this.props.getScrollPosition}
      />;
    }

    let widget_styles = {
      display: 'inline-block',
      marginLeft: '3px',
      borderRadius: '3px',
      marginLeft: '2px',
      border: '1px solid transparent',
    };

    let hovering_class = 'not-hovering';
    if (this.state.hovering || this.state.dropdown_open) {
      widget_styles.border = '1px solid silver';
      widget_styles.cursor = 'pointer';
      hovering_class = 'is-hovering';
    }

    // tabIndex is set to zero only when the dropdown is not open so that
    // keyboard navigaton doesn't try to move focus to this element.
    let tabIndex = (this.state.dropdown_open) ? null : "0";

    return (
      <span className="step_target_widget__dropdown">
        <span
          className="step_target_widget__dropdown_head"
          tabIndex={tabIndex}
          ref={this.dropdownHead}
          style={widget_styles}
          onClick={this.handleToggleDropdown}
          onKeyDown={this.handleToggleDropdown}
          onFocus={this.handleDropDownWidgetFocus}
          onBlur={this.handleDropDownWidgetBlur}
          onMouseEnter={this.handleDropDownWidgetMouseEnter}
          onMouseLeave={this.handleDropDownWidgetMouseLeave}
        >
          <span style={{padding: '0 3px'}}><span className="icon pencil-icon">+</span></span>
        </span>
        {dropdown_list}
      </span>
    );

  }

}



class StepSwimEquipmentWidget extends DropDownWidget {

  render() {

    let dropdown_list;
    if (this.state.dropdown_open) {
      dropdown_list = <StepSwimEquipmentDropDownList
        discipline={this.props.discipline}
        step={this.props.step}
        updateStructuredTrainingSteps={this.props.updateStructuredTrainingSteps}
        handleCloseDropdown={this.handleCloseDropdown}
        formChanged={this.props.formChanged}
        getScrollPosition={this.props.getScrollPosition}
      />;
    }

    let widget_head = <StepSwimEquipmentWidgetHead
      menu_item_selected={this.state.menu_item_selected}
      step={this.props.step}
      updateStructuredTrainingSteps={this.props.updateStructuredTrainingSteps}
      formChanged={this.props.formChanged}
    />

    let widget_styles = {
      display: 'inline-block',
      marginLeft: '3px',
      borderRadius: '3px',
      marginLeft: '2px',
      border: '1px solid transparent',
    };

    let hovering_class = 'not-hovering';
    if (this.state.hovering || this.state.dropdown_open) {
      widget_styles.border = '1px solid silver';
      widget_styles.cursor = 'pointer';
      hovering_class = 'is-hovering';
    }

    let dropdown_arrow = <span className={'step_target_widget__dropdown_arrow' + ' step_target_widget__dropdown_arrow--' + hovering_class}></span>;

    // tabIndex is set to zero only when the dropdown is not open so that
    // keyboard navigaton doesn't try to move focus to this element.
    let tabIndex = (this.state.dropdown_open) ? null : "0";

    return (
      <span className="step_target_widget__dropdown">
        <span
          className="step_target_widget__dropdown_head"
          tabIndex={tabIndex}
          ref={this.dropdownHead}
          style={widget_styles}
          onClick={this.handleToggleDropdown}
          onKeyDown={this.handleToggleDropdown}
          onFocus={this.handleDropDownWidgetFocus}
          onBlur={this.handleDropDownWidgetBlur}
          onMouseEnter={this.handleDropDownWidgetMouseEnter}
          onMouseLeave={this.handleDropDownWidgetMouseLeave}
        >
          <span style={{padding: '0 3px'}}>{widget_head}</span>
          {dropdown_arrow}
        </span>
        {dropdown_list}
      </span>
    );

  }

}

class StepSwimStrokeWidget extends DropDownWidget {

  render() {

    let dropdown_list;
    if (this.state.dropdown_open) {
      dropdown_list = <StepSwimStrokeDropDownList
        discipline={this.props.discipline}
        step={this.props.step}
        updateStructuredTrainingSteps={this.props.updateStructuredTrainingSteps}
        handleCloseDropdown={this.handleCloseDropdown}
        formChanged={this.props.formChanged}
        getScrollPosition={this.props.getScrollPosition}
      />;
    }

    let swim_stroke_widget = <StepSwimStrokeWidgetHead
      menu_item_selected={this.state.menu_item_selected}
      step={this.props.step}
      updateStructuredTrainingSteps={this.props.updateStructuredTrainingSteps}
      formChanged={this.props.formChanged}
    />

    let widget_styles = {
      display: 'inline-block',
      marginLeft: '3px',
      borderRadius: '3px',
      marginLeft: '2px',
      border: '1px solid transparent',
    };

    let hovering_class = 'not-hovering';
    if (this.state.hovering || this.state.dropdown_open) {
      widget_styles.border = '1px solid silver';
      widget_styles.cursor = 'pointer';
      hovering_class = 'is-hovering';
    }

    let dropdown_arrow = <span className={'step_target_widget__dropdown_arrow' + ' step_target_widget__dropdown_arrow--' + hovering_class}></span>;

    // tabIndex is set to zero only when the dropdown is not open so that
    // keyboard navigaton doesn't try to move focus to this element.
    let tabIndex = (this.state.dropdown_open) ? null : "0";

    return (
      <span className="step_target_widget__dropdown">
        <span
          className="step_target_widget__dropdown_head"
          tabIndex={tabIndex}
          ref={this.dropdownHead}
          style={widget_styles}
          onClick={this.handleToggleDropdown}
          onKeyDown={this.handleToggleDropdown}
          onFocus={this.handleDropDownWidgetFocus}
          onBlur={this.handleDropDownWidgetBlur}
          onMouseEnter={this.handleDropDownWidgetMouseEnter}
          onMouseLeave={this.handleDropDownWidgetMouseLeave}
        >
          <span style={{padding: '0 3px'}}>{swim_stroke_widget}</span>
          {dropdown_arrow}
        </span>
        {dropdown_list}
      </span>
    );

  }

}

class WidgetDropDownList extends React.Component {

  keydown_timeout = null;
  blur_timeout = null;

  constructor(props) {
    super(props);

    this.state = {
      highlighted_option: this.getSelectedOptionValueFromStep(),
      top_position: null,
      bottom_position: null,
    };

    this.dropdownList = React.createRef();
  }

  getSelectedOptionValueFromStep = () => {
    return null;
  }

  getHighlightedOption = () => {
    let dropdown_row_data = this.getDropdownRows();
    let current_option = 0;
    for (let i = 0; i < dropdown_row_data.length; i++) {

      if (dropdown_row_data[i].element_type != 'option') {
        continue;
      }

      if (current_option == this.state.highlighted_option) {
        return dropdown_row_data[i];
      }

      current_option++;

    }
  }

  componentDidMount = () => {

    if (this.dropdownList.current) {
      this.dropdownList.current.focus();
    }

    this.setDropdownListPosition();
  }

  componentWillUnmount() {
    if (this.blur_timeout !== null) {
      clearTimeout(this.blur_timeout);
    }
    if (this.keydown_timeout !== null) {
      clearTimeout(this.keydown_timeout);
    }
  }

  setDropdownListPosition = () => {

    // This is the height of the main page header which will obscure the
    // dropdown. To better fix this we would have to render the dropdown list
    // at the top level of the DOM.
    const TOP_MENU_HEIGHT = 91;

    let viewport_height = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)
    let dropdown_position_data = this.dropdownList.current.getBoundingClientRect();
    let scroll_position = this.props.getScrollPosition();
    let height_of_dropdown = dropdown_position_data.height;
    let position_of_bottom_of_dropdown = viewport_height - (dropdown_position_data.bottom - scroll_position);

    let desired_position_of_bottom = ((position_of_bottom_of_dropdown < 0) ? 3 : position_of_bottom_of_dropdown) + 'px';
    let desired_position_of_top = (height_of_dropdown > (viewport_height - TOP_MENU_HEIGHT)) ? (TOP_MENU_HEIGHT + 3) + 'px' : null;

    let new_state = {bottom_position: desired_position_of_bottom, top_position: desired_position_of_top}

    this.setState(new_state);

  }

  handleKeyDown = (event) => {

    if (event.type == 'keydown') {

      let key = event.key || event.keyCode;

      // Enter or space key selects an item.
      if (key === 'Enter' || key == 13 || key == ' ' || key == 32) {
        let highlighted_option = this.getHighlightedOption();

        // The timeout is needed because otherwise the enter key will
        // end up being processed by the element that then gets focus
        // which can sometimes cause the menu to open again.
        this.keydown_timeout = setTimeout(() => {
          this.handleDropdownOptionSelected(highlighted_option);
        }, 1);

      }

      // Escape closes the dropdown.
      else if (key === 'Escape' || key === 'Esc' || key === 27) {
        this.props.handleCloseDropdown(true);
      }

      // Cycle down through the list items.
      else if (key === 'ArrowDown' || key === 40) {
        let new_highlighted_option = this.state.highlighted_option + 1;
        if (new_highlighted_option < this.getOptionsLength()) {
          this.setState({highlighted_option: new_highlighted_option});
        }
      }

      // Cycle up through the list items.
      else if (key === 'ArrowUp' || key === 38) {
        let new_highlighted_option = this.state.highlighted_option - 1;
        if (new_highlighted_option >= 0) {
          this.setState({highlighted_option: new_highlighted_option});
        }
      }

    }

  }

  handleClickDropdownOption = (option_data, event) => {
    if (isDropdownOpenEvent(event) === true) {
      this.handleDropdownOptionSelected(option_data);
    }
  }

  handleDropdownOptionSelected = (option_data) => {
    let step = this.updateStepWithSelectedOption(option_data, this.props.step);
    step.markSectionHasChanges();
    this.props.updateStructuredTrainingSteps([step]);
    this.props.handleCloseDropdown(true);
    this.props.formChanged();
  }

  handleBlur = (event) => {
    // The timeout is in place to avoid the dropdown closing and then the
    // click event firing on the widget head afterwards and reopening the
    // dropdown. This won't be reliable, but 200 milliseconds seems to be
    // enough to stop the behavior mostly. Unfortunately because we change
    // the tabIndex to improve keyboard navigation the event.relatedTarget
    // value ends up being null so we can't check that. We can't check that
    // the dropdown is open because we are closing it first. We can't remove
    // the click event on the widget 'head' because it activates again as
    // soon as we close on the render step.
    this.blur_timeout = setTimeout(() => {
      this.props.handleCloseDropdown(false);
    }, 200);
  }

  render() {

    let dropdown_style = {
      position: 'fixed',
      background: 'white',
      borderRadius: '5px',
      top: this.state.top_position,
      bottom: this.state.bottom_position,
      left: '93px',
      marginTop: '2px',
      boxShadow: '0px 0px 6px -3px #757374',
      border: '1px solid silver',
      overflow: 'auto',
      zIndex: 1000, // This doesn't work to bring it in view over the top menu bar. To do that I think I need to render this at the top level.
    }

    let dropdown_row_data = this.getDropdownRows();

    let dropdown_row_elements = [];
    let options_counter = 0;
    for (let i = 0; i < dropdown_row_data.length; i++) {
      if (dropdown_row_data[i].element_type == 'option') {

        let class_name = "step_target_widget__dropdown_list_item";
        if (this.state.highlighted_option == options_counter) {
          class_name += " step_target_widget__dropdown_list_item--selected";
        }

        dropdown_row_elements.push(
          <div
            key={i}
            className={class_name}
            onClick={this.handleClickDropdownOption.bind(null, dropdown_row_data[i])}>
              {dropdown_row_data[i].contents}
          </div>
        );

        options_counter++;

      }
      else if (dropdown_row_data[i].element_type == 'section_title') {
        dropdown_row_elements.push(<div key={i} style={{padding: '9px 0px 0px 6px', fontWeight: 'bold', fontVariant: 'small-caps'}}>{dropdown_row_data[i].title}</div>);
      }
    }


    if (this.getOptionsLength() != options_counter) {
      // If this throws, the keyboard highlighting won't highlight the correct
      // number of options.
      throw 'getOptionsLength() does not match options counter on render.'
    }

    let dropdown_list = (
      <div
       className="step_target_widget__dropdown_list"
       ref={this.dropdownList}
       style={dropdown_style}
       onKeyDown={this.handleKeyDown}
       onBlur={this.handleBlur}
       tabIndex="0"
      >
       {dropdown_row_elements}
      </div>
    );

    return dropdown_list;

  }

}

class StepSwimStrokeDropDownList extends WidgetDropDownList {

  getSelectedOptionValueFromStep = () => {
    return this.props.step.target_value;
  }

  getOptionsLength = () => {
    return Object.keys(SWIM_STROKES).length;
  }

  updateStepWithSelectedOption = (option_data, step) => {
    step.setSwimStroke(option_data.swim_stroke_id)
    return step;
  }

  getDropdownRows = () => {

    let dropdown_row_data = [];

    Object.keys(SWIM_STROKES).forEach((swim_stroke_id) =>{
      dropdown_row_data.push({
        element_type: 'option',
        swim_stroke_id: swim_stroke_id,
        contents: <span>{SWIM_STROKES[swim_stroke_id]}</span>
      });
    });

    return dropdown_row_data;
  }

}

class EditStepPopupDropDown extends WidgetDropDownList {

  getSelectedOptionValueFromStep = () => {
    return (this.props.step.equipment_ids) ? this.props.step.equipment_ids : 0;
  }

  getOptionsLength = () => {
    return 4;
  }

  updateStepWithSelectedOption = (option_data, step) => {

    if (option_data.option_name == 'equipment') {
      step.setSwimEquipment(option_data.equipment_ids);
    }

    else if (option_data.option_name == 'stroke') {
      step.setSwimStroke(option_data.stroke_id);
    }

    else if (option_data.option_name == 'proceed_off') {
      step.setProceedOffSeconds(option_data.proceed_off_seconds);
    }

    else if (option_data.option_name == 'step_rest_interval') {
      step.setStepRestInterval(option_data.step_rest_interval);
    }

    return step;
  }

  getDropdownRows = () => {

    let dropdown_row_data = [];

    dropdown_row_data.push({
      element_type: 'option',
      option_name: 'equipment',
      equipment_ids: (this.props.step.equipment_ids[0] === SWIM_EQUIPMENT_INACTIVE) ? SWIM_EQUIPMENT_NO_EQUIPMENT : SWIM_EQUIPMENT_INACTIVE,
      contents: <span>{(this.props.step.equipment_ids[0] === SWIM_EQUIPMENT_INACTIVE) ? 'Add equipment' : 'Remove equipment'}</span>
    });

    dropdown_row_data.push({
      element_type: 'option',
      option_name: 'stroke',
      stroke_id: (this.props.step.getSwimStrokeAsId() === null) ? 0 : null,
      contents: <span>{(this.props.step.getSwimStrokeAsId() === null) ? 'Add stroke' : 'Remove stroke'}</span>
    });

    dropdown_row_data.push({
      element_type: 'option',
      option_name: 'proceed_off',
      proceed_off_seconds: (this.props.step.proceed_off_seconds === null) ? 0 : null,
      contents: <span>{(this.props.step.proceed_off_seconds === null) ? 'Add \'swim off\' timing' : 'Remove \'swim off\' timing'}</span>
    });

    dropdown_row_data.push({
      element_type: 'option',
      option_name: 'step_rest_interval',
      step_rest_interval: (this.props.step.step_rest_interval === null) ? 0 : null,
      contents: <span>{(this.props.step.step_rest_interval === null) ? 'Add rest interval' : 'Remove rest interval'}</span>
    });

    return dropdown_row_data;
  }

}

class StepSwimEquipmentDropDownList extends WidgetDropDownList {

  getSelectedOptionValueFromStep = () => {
    return (this.props.step.equipment_ids) ? this.props.step.equipment_ids : 0;
  }

  getOptionsLength = () => {
    return Object.keys(SWIM_EQUIPMENT).length;
  }

  updateStepWithSelectedOption = (option_data, step) => {
    step.toggleSwimEquipment(option_data.equipment_id)
    return step;
  }

  getDropdownRows = () => {

    let dropdown_row_data = [];

    Object.keys(SWIM_EQUIPMENT).forEach((swim_equipment_id) =>{
      dropdown_row_data.push({
        element_type: 'option',
        equipment_id: swim_equipment_id,
        contents: <span>{SWIM_EQUIPMENT[swim_equipment_id]}</span>
      });
    });

    return dropdown_row_data;
  }

}

class StepTargetWidget extends DropDownWidget {

  getTargetValueHigh = () => {
    if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER) {
      if (!isNaN(this.props.step.custom_target_value_high) && this.props.step.custom_target_value_high !== null) {
        return this.props.step.custom_target_value_high;
      }
    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_PERCENTAGE_FTP) {
      if (this.props.step.custom_target_value_high < 1000 && this.props.step.custom_target_value_high >= 0) {
        return this.props.step.custom_target_value_high;
      }
    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED) {
      if (this.props.distance_unit_preference == 'kilometers') {
        return metersPerSecondAsSecondsPerKilometer(this.props.step.custom_target_value_high / 1000)
      }
      else {
        return metersPerSecondAsSecondsPerMile(this.props.step.custom_target_value_high / 1000)
      }
    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE) {
      if (!isNaN(this.props.step.custom_target_value_high) && this.props.step.custom_target_value_high !== null) {
        return this.props.step.custom_target_value_high;
      }
    }
    return '';
  }

  render() {

    let dropdown_list;
    if (this.state.dropdown_open) {
      dropdown_list = <StepTargetWidgetDropDownList
        discipline={this.props.discipline}
        step={this.props.step}
        custom_zones={this.props.custom_zones}
        value={this.getTargetValueHigh()}
        target_type_category={this.props.target_type_category}
        distance_unit_preference={this.props.distance_unit_preference}
        updateStructuredTrainingSteps={this.props.updateStructuredTrainingSteps}
        handleCloseDropdown={this.handleCloseDropdown}
        formChanged={this.props.formChanged}
        getScrollPosition={this.props.getScrollPosition}
      />;
    }

    let target_widget;
    if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER) {

      target_widget = <StepTargetWidgetHeadPower
        menu_item_selected={this.state.menu_item_selected}
        value={this.getTargetValueHigh()}
        step={this.props.step}
        updateStructuredTrainingSteps={this.props.updateStructuredTrainingSteps}
        formChanged={this.props.formChanged}
      />

    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_PERCENTAGE_FTP) {
      target_widget = <StepTargetWidgetHeadPercentageFTP
        menu_item_selected={this.state.menu_item_selected}
        value={this.getTargetValueHigh()}
        step={this.props.step}
        updateStructuredTrainingSteps={this.props.updateStructuredTrainingSteps}
        formChanged={this.props.formChanged}
      />

    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE) {
      target_widget = <StepTargetWidgetHeadPowerZone
        menu_item_selected={this.state.menu_item_selected}
        step={this.props.step}
        custom_zones={this.props.custom_zones}
        focusDropdownHead={this.focusDropdownHead}
      />
    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE) {
      target_widget = <StepTargetWidgetHeadHeartRate
        menu_item_selected={this.state.menu_item_selected}
        value={this.getTargetValueHigh()}
        step={this.props.step}
        updateStructuredTrainingSteps={this.props.updateStructuredTrainingSteps}
        formChanged={this.props.formChanged}
      />
    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE_BY_ZONE) {
      target_widget = <StepTargetWidgetHeadHeartRateZone
        menu_item_selected={this.state.menu_item_selected}
        step={this.props.step}
        custom_zones={this.props.custom_zones}
        distance_unit_preference={this.props.distance_unit_preference}
        focusDropdownHead={this.focusDropdownHead}
      />
    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_OPEN) {
      target_widget = <StepTargetWidgetHeadOpen
        menu_item_selected={this.state.menu_item_selected}
        step={this.props.step}
      />
    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED) {
      target_widget = <StepTargetWidgetHeadSpeed
        menu_item_selected={this.state.menu_item_selected}
        value={this.getTargetValueHigh()}
        distance_unit_preference={this.props.distance_unit_preference}
        step={this.props.step}
        updateStructuredTrainingSteps={this.props.updateStructuredTrainingSteps}
        formChanged={this.props.formChanged}
      />
    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED_BY_ZONE) {
      target_widget = <StepTargetWidgetHeadSpeedZone
        menu_item_selected={this.state.menu_item_selected}
        step={this.props.step}
        custom_zones={this.props.custom_zones}
        distance_unit_preference={this.props.distance_unit_preference}
        focusDropdownHead={this.focusDropdownHead}
      />
    }
    else {
      throw 'Invalid target input type' + this.props.step.target_type
    }

    let widget_styles = {
      display: 'inline-block',
      marginLeft: '3px',
      borderRadius: '3px',
      marginLeft: '2px',
      border: '1px solid transparent',
    };

    let hovering_class = 'not-hovering';
    if (this.state.hovering || this.state.dropdown_open) {
      widget_styles.border = '1px solid silver';
      widget_styles.cursor = 'pointer';
      hovering_class = 'is-hovering';
    }

    let dropdown_arrow = <span className={'step_target_widget__dropdown_arrow' + ' step_target_widget__dropdown_arrow--' + hovering_class}></span>;

    // tabIndex is set to zero only when the dropdown is not open so that
    // keyboard navigaton doesn't try to move focus to this element.
    let tabIndex = (this.state.dropdown_open) ? null : "0";

    return (
      <span className="step_target_widget__dropdown">
        <span
          className="step_target_widget__dropdown_head"
          tabIndex={tabIndex}
          ref={this.dropdownHead}
          style={widget_styles}
          onClick={this.handleToggleDropdown}
          onKeyDown={this.handleToggleDropdown}
          onFocus={this.handleDropDownWidgetFocus}
          onBlur={this.handleDropDownWidgetBlur}
          onMouseEnter={this.handleDropDownWidgetMouseEnter}
          onMouseLeave={this.handleDropDownWidgetMouseLeave}
        >
          <span style={{padding: '0 3px'}}>{target_widget}</span>
          {dropdown_arrow}
        </span>
        {dropdown_list}
      </span>
    );

  }

}

class StepTargetWidgetDropDownList extends WidgetDropDownList {

  getSelectedOptionValueFromStep = () => {
    let dropdown_row_data = this.getDropdownRows();

    let option_index = 0;
    for (let i = 0; i < dropdown_row_data.length; i++) {
      if (dropdown_row_data[i].element_type == 'option') {
        if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE) {
          if (this.props.step.zone == dropdown_row_data[i].zone_id) {
            return option_index;
          }
        }
        else if (this.props.step.target_type == dropdown_row_data[i].target_type) {
          return option_index;
        }
        option_index++;
      }
    }

  }

  updateStepWithSelectedOption = (option_data, step) => {
    step.target_type = option_data.target_type;
    if (option_data.zone_id) {
      step.zone = option_data.zone_id;
    }
    return step;
  }

  getOptionsLength = () => {

    // This needs updating when more options are added.
    if (this.props.discipline == WORKOUT_DISCIPLINES.SWIMMING) {
      // Number of default types. Currently no zones associated with swim.
      return 2;
    }
    else if (this.props.discipline == WORKOUT_DISCIPLINES.RUNNING) {
      // Number of default types + 5 normal zones + however many custom zones;
      return 2 + 5 + this.props.custom_zones.length;
    }
    else if (this.props.discipline == WORKOUT_DISCIPLINES.CYCLING) {
      // Number of default types + 5 normal zones + however many custom zones;
      return 3 + 5 + this.props.custom_zones.length;
    }
    else {
      throw("Invalid discipline in getOptionsLength");
    }

  }

  getDropdownRows = () => {

    if (this.props.discipline == WORKOUT_DISCIPLINES.SWIMMING) {
      return this.getDropdownRowsSwimming();
    }
    else if (this.props.discipline == WORKOUT_DISCIPLINES.RUNNING) {
      return this.getDropdownRowsRunning();
    }
    else if (this.props.discipline == WORKOUT_DISCIPLINES.CYCLING) {
      return this.getDropdownRowsCycling();
    }
    else {
      throw("Invalid discipline in getDropdownRows");
    }

  }

  getDropdownRowsRunning = () => {

    let dropdown_row_data = [];

    if (this.props.target_type_category == 'pace') {
      const unit = (this.props.distance_unit_preference == 'kilometers') ? 'min/km' : 'min/mile';
      dropdown_row_data.push({
        element_type: 'option',
        target_type: STEP_CONSTANTS.TARGET_TYPE_SPEED,
        zone_id: null,
        contents: <span>at <DurationInput format="MM:SS" className="structured_training__step-target-value session-form__number-field" value={this.props.value} style={{background: INPUT_BACKGROUND_COLOR, width: '3em', border: '0', padding: '2px', margin: '2px 0'}} readOnly disabled /> {unit}</span>
      });
    }
    else if (this.props.target_type_category == 'heart rate') {
      dropdown_row_data.push({
        element_type: 'option',
        target_type: STEP_CONSTANTS.TARGET_TYPE_HEART_RATE,
        zone_id: null,
        contents: <span>at <input type="number" className="session-form__number-field" style={{ background: INPUT_BACKGROUND_COLOR, width: '2.5em', border: 0, padding: '2px'}} value={this.props.value} readOnly disabled /> bpm</span>
      });
    }

    dropdown_row_data.push({
      element_type: 'option',
      target_type: STEP_CONSTANTS.TARGET_TYPE_OPEN,
      zone_id: null,
      contents: <span>at no target intensity</span>
    });

    dropdown_row_data.push({
      element_type: 'section_title',
      title: 'zones',
    });

    let by_zone_target_type;
    if (this.props.target_type_category == 'pace') {
      by_zone_target_type = STEP_CONSTANTS.TARGET_TYPE_SPEED_BY_ZONE;
    }
    else if (this.props.target_type_category == 'heart rate') {
      by_zone_target_type = STEP_CONSTANTS.TARGET_TYPE_HEART_RATE_BY_ZONE;
    }

    for (let i = 1; i <= 5; i++) {
      dropdown_row_data.push({
        element_type: 'option',
        target_type: by_zone_target_type,
        zone_id: i,
        contents: 'zone ' + i,
      });
    }

    for (let i = 0; i < this.props.custom_zones.length; i++) {
      dropdown_row_data.push({
        element_type: 'option',
        target_type: by_zone_target_type,
        zone_id: this.props.custom_zones[i].id,
        contents: this.props.custom_zones[i].name,
      });
    }

    return dropdown_row_data;
  }

 getDropdownRowsSwimming = () => {

    let dropdown_row_data = [];

    if (this.props.target_type_category == 'pace') {
      const unit = 'min/100m'
      dropdown_row_data.push({
        element_type: 'option',
        target_type: STEP_CONSTANTS.TARGET_TYPE_SPEED,
        zone_id: null,
        contents: <span>at <DurationInput format="MM:SS" className="structured_training__step-target-value session-form__number-field" value={this.props.value} style={{background: INPUT_BACKGROUND_COLOR, width: '3em', border: '0', padding: '2px', margin: '2px 0'}} readOnly disabled /> {unit}</span>
      });
    }

    dropdown_row_data.push({
      element_type: 'option',
      target_type: STEP_CONSTANTS.TARGET_TYPE_OPEN,
      zone_id: null,
      contents: <span>at no target intensity</span>
    });

    return dropdown_row_data;

  }

  getDropdownRowsCycling = () => {

    let dropdown_row_data = [];

    dropdown_row_data.push({
      element_type: 'option',
      target_type: STEP_CONSTANTS.TARGET_TYPE_POWER,
      zone_id: null,
      contents: <span>at <input type="number" className="session-form__number-field" style={{ background: INPUT_BACKGROUND_COLOR, width: '2.5em', border: 0, padding: '2px'}} value={this.props.value} readOnly disabled /> watts</span>
    });

    dropdown_row_data.push({
      element_type: 'option',
      target_type: STEP_CONSTANTS.TARGET_TYPE_POWER_PERCENTAGE_FTP,
      zone_id: null,
      contents: <span>at <input type="number" className="session-form__number-field" style={{ background: INPUT_BACKGROUND_COLOR, width: '2.5em', border: 0, padding: '2px'}} value={this.props.value} readOnly disabled /> <span>% FTP</span></span>
    });

    dropdown_row_data.push({
      element_type: 'option',
      target_type: STEP_CONSTANTS.TARGET_TYPE_OPEN,
      zone_id: null,
      contents: <span>at no target intensity</span>
    });

    dropdown_row_data.push({
      element_type: 'section_title',
      title: 'zones',
    });

    for (let i = 1; i <= 5; i++) {
      dropdown_row_data.push({
        element_type: 'option',
        target_type: STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE,
        zone_id: i,
        contents: 'zone ' + i,
      });
    }

    for (let i = 0; i < this.props.custom_zones.length; i++) {
      dropdown_row_data.push({
        element_type: 'option',
        target_type: STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE,
        zone_id: this.props.custom_zones[i].id,
        contents: this.props.custom_zones[i].name,
      });
    }

    return dropdown_row_data;

  }

}

class DropDownWidgetHead extends React.Component {

  constructor(props) {
    super(props);
    this.textInput = React.createRef();
  }

  selectInputElement = () => {
    if (this.textInput.current) {
      this.textInput.current.select();
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.menu_item_selected && this.props.menu_item_selected !== prevProps.menu_item_selected) {
      this.selectInputElement();
    }
  }

  componentDidMount = () => {
    if (this.props.menu_item_selected) {
      this.selectInputElement();
    }
  }

  stopPropagation = (event) => {
    event.stopPropagation();
  }

}

class StepSwimEquipmentWidgetHead extends DropDownWidgetHead {
  render() {
    return <span>{this.props.step.getSwimEquipmentAsText()}</span>
  }
}

class StepSwimStrokeWidgetHead extends DropDownWidgetHead {
  render() {
    const swim_stroke = (this.props.step.getSwimStrokeAsId() === null) ? 'choose...' : this.props.step.getSwimStrokeAsText();
    return <span>{swim_stroke}</span>
  }
}

class StepTargetWidgetHeadOpen extends DropDownWidgetHead {
  render() {
    return <span>at no target intensity</span>
  }
}


class StepTargetWidgetHeadSpeed extends DropDownWidgetHead {

  handleTargetPaceChanged = (time) => {
    if (this.props.distance_unit_preference == 'kilometers') {
      this.props.step.setPace(time, 'seconds_per_kilometer')
    }
    else {
      this.props.step.setPace(time, 'seconds_per_mile')
    }
    this.props.step.markSectionHasChanges();
    this.props.updateStructuredTrainingSteps([this.props.step]);
    this.props.formChanged();
  }

  render() {
    const unit = (this.props.distance_unit_preference == 'kilometers') ? 'min/km' : 'min/mile';
    return <span>at <DurationInput
        className="structured_training__step-target-value session-form__number-field"
        ref={this.textInput}
        value={this.props.value || ''}
        onChange={this.handleTargetPaceChanged}
        style={{ background: INPUT_BACKGROUND_COLOR, width: '3em', border: 0, padding: '2px'}}
        format="MM:SS"
      /> <span className="structured_training__step-target-unit">{unit}</span>
    </span>
  }

}

class StepTargetWidgetHeadHeartRate extends DropDownWidgetHead {

  handleTargetBPMChanged = (event) => {
    if (event.target.value >= 0) {
      this.props.step.custom_target_value_low = parseInt(event.target.value);
      this.props.step.custom_target_value_high = parseInt(event.target.value);
      this.props.step.markSectionHasChanges();
      this.props.updateStructuredTrainingSteps([this.props.step]);
      this.props.formChanged();
    }
  }

  render() {
    return <span>at <input
        ref={this.textInput}
        type="number"
        style={{ background: INPUT_BACKGROUND_COLOR, width: '2.5em', border: 0, padding: '2px'}}
        className="session-form__number-field structured_training__step-target-value"
        onClick={this.stopPropagation}
        onKeyDown={this.stopPropagation}
        onChange={this.handleTargetBPMChanged}
        value={this.props.value || ''}
      /> <span className="structured_training__step-target-unit">bpm</span>
    </span>
  }

}

class StepTargetWidgetHeadPower extends DropDownWidgetHead {

  handleTargetWattsChanged = (event) => {
    if (event.target.value >= 0) {
      this.props.step.custom_target_value_low = parseInt(event.target.value);
      this.props.step.custom_target_value_high = parseInt(event.target.value);
      this.props.step.markSectionHasChanges();
      this.props.updateStructuredTrainingSteps([this.props.step]);
      this.props.formChanged();
    }
  }

  render() {
    return <span>at <input
        ref={this.textInput}
        type="number"
        style={{ background: INPUT_BACKGROUND_COLOR, width: '2.5em', border: 0, padding: '2px'}}
        className="session-form__number-field structured_training__step-target-value"
        onClick={this.stopPropagation}
        onKeyDown={this.stopPropagation}
        onChange={this.handleTargetWattsChanged}
        value={this.props.value || ''}
      /> <span className="structured_training__step-target-unit">watts</span>
    </span>
  }

}

class StepTargetWidgetHeadPercentageFTP extends DropDownWidgetHead {

  handleTargetPercentageFTPChanged = (event) => {
    if (event.target.value >= 0 && event.target.value < 1000) {
      this.props.step.custom_target_value_low = parseInt(event.target.value);
      this.props.step.custom_target_value_high = parseInt(event.target.value);
      this.props.step.markSectionHasChanges();
      this.props.updateStructuredTrainingSteps([this.props.step]);
      this.props.formChanged();
    }
  }

  render() {
    return <span>at <input
          ref={this.textInput}
          type="number"
          style={{ background: INPUT_BACKGROUND_COLOR, width: '2.5em', border: 0, padding: '2px'}}
          className="session-form__number-field"
          onClick={this.stopPropagation}
          onKeyDown={this.stopPropagation}
          onChange={this.handleTargetPercentageFTPChanged}
          value={this.props.value || ''}
        /> <span className="structured_training__step-target-unit">% FTP</span>
      </span>
  }
}

class StepTargetWidgetHeadHeartRateZone extends React.Component {

  constructor(props) {
    super(props);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.menu_item_selected && this.props.menu_item_selected !== prevProps.menu_item_selected) {
      this.props.focusDropdownHead();
    }
  }

  componentDidMount = () => {
    if (this.props.menu_item_selected) {
      this.props.focusDropdownHead();
    }
  }

  render() {

    let target_widget;
    if (this.props.step.zone) {
      if (this.props.step.zone <= 5) {
        target_widget = <span style={{display: 'inline-block', padding: '2px 0'}}>in zone {this.props.step.zone}</span>
      }
      else {
        for (let i = 0; i < this.props.custom_zones.length; i++) {
          if (this.props.step.zone == this.props.custom_zones[i].id) {
            target_widget = <span style={{display: 'inline-block', padding: '2px 0'}}>in {this.props.custom_zones[i].name}</span>;
            break;
          }
        }
      }
    }

    if (!target_widget) {
      target_widget = <span style={{display: 'inline-block', padding: '2px 0'}}>Select target</span>;
    }

    return target_widget;
  }

}

class StepTargetWidgetHeadPowerZone extends React.Component {

  constructor(props) {
    super(props);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.menu_item_selected && this.props.menu_item_selected !== prevProps.menu_item_selected) {
      this.props.focusDropdownHead();
    }
  }

  componentDidMount = () => {
    if (this.props.menu_item_selected) {
      this.props.focusDropdownHead();
    }
  }

  render() {

    let target_widget;
    if (this.props.step.zone) {
      if (this.props.step.zone <= 5) {
        target_widget = <span style={{display: 'inline-block', padding: '2px 0'}}>in zone {this.props.step.zone}</span>
      }
      else {
        for (let i = 0; i < this.props.custom_zones.length; i++) {
          if (this.props.step.zone == this.props.custom_zones[i].id) {
            target_widget = <span style={{display: 'inline-block', padding: '2px 0'}}>in {this.props.custom_zones[i].name}</span>;
            break;
          }
        }
      }
    }

    if (!target_widget) {
      target_widget = <span style={{display: 'inline-block', padding: '2px 0'}}>Select target</span>;
    }

    return target_widget;
  }

}

class StepTargetWidgetHeadSpeedZone extends React.Component {

  constructor(props) {
    super(props);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.menu_item_selected && this.props.menu_item_selected !== prevProps.menu_item_selected) {
      this.props.focusDropdownHead();
    }
  }

  componentDidMount = () => {
    if (this.props.menu_item_selected) {
      this.props.focusDropdownHead();
    }
  }

  render() {

    let target_widget;
    if (this.props.step.zone) {
      if (this.props.step.zone <= 5) {
        target_widget = <span style={{display: 'inline-block', padding: '2px 0'}}>in zone {this.props.step.zone}</span>
      }
      else {
        for (let i = 0; i < this.props.custom_zones.length; i++) {
          if (this.props.step.zone == this.props.custom_zones[i].id) {
            target_widget = <span style={{display: 'inline-block', padding: '2px 0'}}>in {this.props.custom_zones[i].name}</span>;
            break;
          }
        }
      }
    }

    if (!target_widget) {
      target_widget = <span style={{display: 'inline-block', padding: '2px 0'}}>Select target</span>;
    }

    return target_widget;
  }

}


class Graph extends React.Component {

  /**
   * This gets the data with repeat steps duplicated.
   */
  getExpandedData = (nested_steps) => {

    let data = [];

    let y_axis_metric = null;
    let total_seconds = 0;
    let total_km = 0;
    for (let i = 0; i < nested_steps.length; i++) {

      let repeats = 1;
      if (nested_steps[i].repeat_step_data) {
        repeats = nested_steps[i].repeat_step_data.target_value;
      }

      for (let j = 0; j < repeats; j++) {

        for (let k = 0; k < nested_steps[i].steps.length; k++) {

          let step = nested_steps[i].steps[k];

          let watts;
          let ftp_percentage;
          let heart_rate;
          let seconds_per_mile;
          let seconds_per_kilometer;
          if (step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER) {
            watts = step.getTargetWatts(this.props.athlete_data.bike_ftp);
            ftp_percentage = step.getTargetPercentageFTP(this.props.athlete_data.bike_ftp);
            y_axis_metric = 'watts';
          }
          else if (step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE) {
            let zoneData = step.getZoneData(this.props.athlete_data.training_zones, this.props.discipline)
            watts = (zoneData) ? (zoneData.power_min + zoneData.power_max) / 2 : 0;
            y_axis_metric = 'watts';
          }
          else if (step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_PERCENTAGE_FTP) {
            if (!y_axis_metric) y_axis_metric = 'ftp_percentage';
            watts = this.props.athlete_data ? step.getTargetWatts(this.props.athlete_data.bike_ftp) : null;
            ftp_percentage = this.props.athlete_data ? step.getTargetPercentageFTP(this.props.athlete_data.bike_ftp) : null;
          }
          else if (step.target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE) {
            heart_rate = step.getTargetHeartRate();
            y_axis_metric = 'heart_rate';
          }
          else if (step.target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE_BY_ZONE) {
            heart_rate = step.getTargetHeartRate(this.props.athlete_data.training_zones, this.props.discipline);
            y_axis_metric = 'heart_rate';
          }
          else if (step.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED) {
            if (this.props.distance_unit_preference == 'kilometers') {
              seconds_per_kilometer = step.getTargetPaceSecondsPerKilometer();
              y_axis_metric = 'seconds_per_kilometer';
            }
            else {
              seconds_per_mile = step.getTargetPaceSecondsPerMile();
              y_axis_metric = 'seconds_per_mile';
            }
          }
          else if (step.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED_BY_ZONE) {
            if (this.props.distance_unit_preference == 'kilometers') {
              seconds_per_kilometer = step.getTargetPaceSecondsPerKilometer(this.props.athlete_data.training_zones, this.props.discipline);
              y_axis_metric = 'seconds_per_kilometer';
            }
            else {
              seconds_per_mile = step.getTargetPaceSecondsPerMile(this.props.athlete_data.training_zones, this.props.discipline);
              y_axis_metric = 'seconds_per_mile';
            }
          }
          else if (step.target_type == STEP_CONSTANTS.TARGET_TYPE_NOTE) {
            continue; // Not used in charts.
          }

          let seconds = step.getDurationInSeconds();
          let km = step.getDurationInKM() * step.getIndividualStepRepetitions(true);

          let step_data = {
            id: step.id,
            step: step,
            start_seconds: total_seconds,
            start_km : total_km,
            seconds: seconds ? seconds : 0,
            km: km ? km : 0,
          }

          if (y_axis_metric == 'watts' || y_axis_metric == 'ftp_percentage') {
            step_data.watts = watts ? watts : 0;
            step_data.ftp_percentage = ftp_percentage ? ftp_percentage : 0;
          }
          else if (y_axis_metric == 'seconds_per_mile') {
            step_data.seconds_per_mile = seconds_per_mile ? seconds_per_mile : null;
          }
          else if (y_axis_metric == 'seconds_per_kilometer') {
            step_data.seconds_per_kilometer = seconds_per_kilometer ? seconds_per_kilometer : null;
          }
          else if (y_axis_metric == 'heart_rate') {
            step_data.heart_rate = heart_rate ? heart_rate : 0;
          }

          data.push(step_data);

          total_seconds = total_seconds + seconds;
          total_km = total_km + km;
        }

      }

    }

    let return_data = {
      total_seconds: total_seconds,
      total_km: total_km,
      y_axis_metric: y_axis_metric,
      step_data: data,
    };

    if (y_axis_metric == 'watts' || y_axis_metric == 'ftp_percentage') {
      return_data.max_watts = max(data, function (d) { return d.watts });
      return_data.max_ftp_percentage = max(data, function (d) { return d.ftp_percentage });
    }
    else if (y_axis_metric == 'seconds_per_mile') {
      return_data.seconds_per_mile_fastest = min(data, function (d) { return d.seconds_per_mile });
      return_data.seconds_per_mile_slowest = max(data, function (d) { return d.seconds_per_mile });
    }
    else if (y_axis_metric == 'seconds_per_kilometer') {
      return_data.seconds_per_kilometer_fastest = min(data, function (d) { return d.seconds_per_kilometer });
      return_data.seconds_per_kilometer_slowest = max(data, function (d) { return d.seconds_per_kilometer });
    }
    else if (y_axis_metric == 'heart_rate') {
      return_data.max_heart_rate = max(data, function (d) { return d.heart_rate });
    }

    return return_data;
  }

  createChart = () => {

    let chart_container_element = ReactDOM.findDOMNode(this);
    let chart_container = select(chart_container_element);
    let svg = chart_container.select('.structured-workout-chart-' + this.props.activity_or_section + '-' + this.props.activity_or_section_id);

    this.margin = {
      'top': 5,
      'right': 0,
      'bottom': 50,
      'left': 35,
    }

    this.container_width = chart_container_element.clientWidth;
    this.container_height = chart_container_element.clientHeight;
    this.chart_height = this.container_height - this.margin.top - this.margin.bottom;
    this.chart_width = this.container_width - this.margin.left - this.margin.right;

    // This is where we would shift with a transform in order to add margins.
    this.chart = svg.append("g")
      .attr("class", "chart-group")
      .attr("transform", "translate(" + this.margin.left + "," + this.margin.top + ")");

    // Range will probably go in a function that updates when the form resizes.
    this.xScale = scaleLinear().range([0, this.chart_width]);
    this.yScale = scaleLinear().range([this.chart_height, 0]);

    this.chart.append('g')
      .attr('class', 'axis y')
      .attr("transform", "translate(-1,0)");

    this.chart.append('g')
      .attr('class', 'axis x duration ' + this.props.context)
      .attr("transform", "translate(-1," + (this.chart_height) + ")");

    let total_duration = this.chart.append('g')
      .attr('class', 'total-duration')
      .attr("transform", "translate("+ (this.chart_width - 0.5) + "," + this.chart_height + ")");

    total_duration.append('line')
          .attr("x1", 0)
          .attr("x2", 0)
          .attr("y1", 0)
          .attr("y2", 43)
          .attr("fill", 'none')
          .attr("stroke", 'black')
          .attr("stroke-width", '1')
          .attr("shape-rendering", 'crispEdges')
          .attr("class", 'total-duration-pointer');

    total_duration.append('text')
          .attr("class", 'total-duration-text')
          .style("font-size", "13px")
          .style("font-weight", "normal")
          .style('text-anchor', 'end')
          .attr("dx", '-5')
          .attr("dy", 42);

    this.chart.append("g").attr("class", "plot-area");

  }

  getTickValuesWithMaxIncluded = () => {
    let rough_number_of_ticks = this.chart_width / 65;

    // Get ticks that D3 calculates for us.
    let ticks = this.xScale.ticks(rough_number_of_ticks);

    // Add the highest value.
    ticks.push(this.xScale.domain()[1]);

    return ticks;
  }

  updateChart = () => {

    let data = this.getExpandedData(this.props.nested_steps);

    let x_axis_type = 'time';
    if (this.props.duration_type == STEP_CONSTANTS.DURATION_TYPE_DISTANCE) {
      x_axis_type = 'distance';
    }

    // Update the scale with max values to display.
    if (x_axis_type == 'time') {
      this.xScale.domain([0, data.total_seconds]);
    }
    else {
      this.xScale.domain([0, data.total_km]);
    }

    if (data.y_axis_metric == 'watts') {
      this.yScale.domain([0, data.max_watts]);
    }
    else if (data.y_axis_metric == 'ftp_percentage') {
      this.yScale.domain([0, data.max_ftp_percentage]);
    }
    else if (data.y_axis_metric == 'seconds_per_mile') {
      // Add 30 seconds to the slowest time so that some of the bar
      // is visible on the scale.
      this.yScale.domain([data.seconds_per_mile_slowest + 30, data.seconds_per_mile_fastest]);
    }
    else if (data.y_axis_metric == 'seconds_per_kilometer') {
      // Add 30 seconds to the slowest time so that some of the bar
      // is visible on the scale.
      this.yScale.domain([data.seconds_per_kilometer_slowest + 30, data.seconds_per_kilometer_fastest]);
    }
    else {
      this.yScale.domain([0, data.max_heart_rate]);
    }

    // Redraw the axis.

    let duration_axis = axisBottom()
      .scale(this.xScale)
      .ticks(this.chart_width / 65);

    if (x_axis_type == 'time') {
      duration_axis.tickFormat(formatTime);
    }
    else if (x_axis_type == 'distance') {
      duration_axis.tickFormat((d) => {return d + 'km'});
    }

    this.chart.select('.axis.duration').call(duration_axis);

    let y_axis = axisLeft().scale(this.yScale).ticks(5);

    if (data.y_axis_metric == 'seconds_per_kilometer' || data.y_axis_metric == 'seconds_per_mile') {
      y_axis.tickFormat(formatTime);
    }

    this.chart.select('.axis.y').call(y_axis);

    // Update total duration text.
    let text;
    if (x_axis_type == 'time') {
      text = 'Total time ' + formatTime(data.total_seconds, true, true);
    }
    else if (x_axis_type == 'distance') {
      text = 'Total distance ' + formatDistance(data.total_km, 2) + 'km';
    }

    this.chart.select('.total-duration-text').text(text);

    // Redraw the bars.

    let existing_bars = this.chart.selectAll(".bar")
      .data(data.step_data)

    existing_bars.exit().remove();

    let new_bars = existing_bars.enter()
      .append('rect')
        .attr("class", "bar")

    let all_bars = existing_bars.merge(new_bars);

    all_bars
      .attr("x", (d) => {
        // cumulative time/distance before this one.
        if (x_axis_type == 'time') {
          return this.xScale(d.start_seconds)
        }
        else if (x_axis_type == 'distance') {
          return this.xScale(d.start_km)
        }
      })
      .attr("width", (d) => {
        // segment time/distance
        if (x_axis_type == 'time') {
          return this.xScale(d.seconds)
        }
        else if (x_axis_type == 'distance') {
          return this.xScale(d.km)
        }
      })
      .attr("y", (d) => {
        if (d[data.y_axis_metric]) {
          // Height - value of y metric.
          return this.yScale(d[data.y_axis_metric]);
        }
        else {
          // Probably a step with an open target. Or the target value hasn't
          // been entered yet. So this will be a bar with zero height.
          return this.chart_height;
        }
      })
      .attr("height", (d) => {
        if (d[data.y_axis_metric]) {
          // height - value of y metric
          return this.chart_height - this.yScale(d[data.y_axis_metric]);
        }
        else {
          // Probably a step with an open target. Or the target value hasn't
          // been entered yet. So this will be a bar with zero height.
          return 0;
        }
      })
      .on("click", (event, d) => {
        if (this.props.setActiveStep && this.props.active_step_id != d.id) {
          this.props.setActiveStep(d.id);
        }
      })
      .style("mask", (d) => {
        if (this.props.active_step_id == d.id) {
          return 'url(#mask-stripe)';
        }
      })
      .style("fill", (d) => {
        return this.getBarColour(d.step)
      });

  }

  getBarColour = (step) => {
    let zone = step.get1to5Zone(this.props.athlete_data?.training_zones, this.props.discipline);
    if (zone) {
      return COLOURS.intensity[zone];
    }
    else {
      return '#d2d2d2';
    }
  }

  componentDidMount = () => {
    this.createChart();
    this.updateChart();
  }

  componentDidUpdate = () => {
    this.updateChart();
  }

  render() {

    let mask;
    if (this.props.context == "editor") {
      mask = (
        <defs>
          <pattern id="pattern-stripe"
            width="4"
            height="4"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)">
            <rect width="3.5" height="4" transform="translate(0,0)" fill="white"></rect>
          </pattern>
          <mask id="mask-stripe">
            <rect x="0" y="0" width="100%" height="100%" fill="url(#pattern-stripe)" />
          </mask>
        </defs>
      );
    }

    return (
      <div style={{height: this.props.height, width: '100%', clear: 'both', marginTop: '15px'}}>
        <svg style={{height: '100%', width: '100%'}} className={"structured-workout-chart-" + this.props.activity_or_section + '-' + this.props.activity_or_section_id}>
          {mask}
        </svg>
      </div>
    );
  }

}

class StructuredTrainingSectionDisplay extends React.Component {

  render () {

    let rendered_steps = [];
    for (var i = 0; i < this.props.section.steps.length; i++) {

      let step = this.props.section.steps[i];

      rendered_steps.push(
        <StructuredTrainingStepDisplay
          key={step.index}
          discipline={this.props.discipline}
          athlete_data={this.props.athlete_data}
          distance_unit_preference={this.props.distance_unit_preference}
          step={step}
          handleOpenZoneSettings={this.props.handleOpenZoneSettings}
        />
      );

    }

    let repeat_section_content = null;
    if (this.props.section.repeat_step_data != null) {

      let section_name;
      if (this.props.section.repeat_step_data.name) {
        section_name = <div style={{marginBottom: '4px', fontWeight: 'bold'}}>{this.props.section.repeat_step_data.name}</div>
      }

      repeat_section_content = (
        <div style={{marginTop: '2px'}}>
        {section_name}
        <div><span style={{fontStyle: 'italic', fontSize: '0.9em'}}>repeat x {this.props.section.repeat_step_data.target_value}</span></div>
        </div>
      );

    }

    return <div>
      <div className="step-section__repeat_data">{repeat_section_content}</div>
      <div className="step-section__steps">{rendered_steps}</div>
    </div>

  }
}

class StructuredTrainingStepDisplay extends React.Component {

  getDurationValue = () => {
    if (this.props.step.duration_value == null) {
      return '';
    }
    else if (this.props.step.duration_type == STEP_CONSTANTS.DURATION_TYPE_TIME) {
      return this.props.step.duration_value / 60;
    }
    else if (this.props.step.duration_type == STEP_CONSTANTS.DURATION_TYPE_DISTANCE) {
      return this.props.step.duration_value / 100000;
    }
    return '';
  }

  getDurationMetric = () => {
    if (this.props.step.duration_type == STEP_CONSTANTS.DURATION_TYPE_DISTANCE) {
      return 'km';
    }
    else if (this.props.step.duration_type == STEP_CONSTANTS.DURATION_TYPE_TIME) {
      return 'seconds';
    }
  }

  getDurationFormatted = () => {
    if (this.props.step.duration_type == STEP_CONSTANTS.DURATION_TYPE_DISTANCE) {
      const km = this.getDurationValue();
      if (this.props.discipline == WORKOUT_DISCIPLINES.SWIMMING ) {
        return this.props.step.getIndividualStepRepetitions() + ' x ' + (km * 1000) + 'm';
      }
      else {
        return km + 'km';
      }
    }
    else if (this.props.step.duration_type == STEP_CONSTANTS.DURATION_TYPE_TIME) {
      return formatTime(this.props.step.duration_value, true, true);
    }
  }

  getTargetValueHigh = () => {
    if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER) {
      return this.props.step.custom_target_value_high;
    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_PERCENTAGE_FTP) {
      if (this.props.step.custom_target_value_high < 1000) {
        return this.props.step.custom_target_value_high;
      }
    }
    return '';
  }

  render () {

    let duration_field;
    let target_fields;

    if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER) {

      let in_ftp_percentage;
      if (this.props.athlete_data && this.props.athlete_data.bike_ftp) {
        let ftp = this.props.step.getTargetPercentageFTP(this.props.athlete_data.bike_ftp);
        if (ftp) {
          in_ftp_percentage = <span className="structured_training__step-converted-to-watts">({decimalPlaces(ftp, 2, 'zero')}% FTP)</span>
        }
      }

      target_fields = (
        <span>
          <span>at</span>&nbsp;
          <span className="structured_training__step-target-value-high">{this.getTargetValueHigh()}</span>&nbsp;
          <span>watts</span> {in_ftp_percentage}
        </span>
      );

      duration_field = <span className="structured_training__step-duration">{this.getDurationFormatted()}</span>;
    }

    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_PERCENTAGE_FTP) {

      let in_watts;
      if (this.props.athlete_data && this.props.athlete_data.bike_ftp) {
        let watts = this.props.step.getTargetWatts(this.props.athlete_data.bike_ftp);
        if (watts) {
          in_watts = <span className="structured_training__step-converted-to-watts">({decimalPlaces(watts, 2, 'zero')} watts)</span>
        }
      }

      target_fields = (
        <span>
          <span>at</span>&nbsp;
          <span className="structured_training__step-target-value-high">{decimalPlaces(this.getTargetValueHigh(), 2, 'empty_string')}</span>
          <span className="structured_training__step-target-unit">% FTP</span> {in_watts}
        </span>
      );

      duration_field = <span className="structured_training__step-duration">{this.getDurationFormatted()}</span>;

    }

    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE) {

      let zone_data = this.props.step.getZoneData(this.props.athlete_data?.training_zones, this.props.discipline);
      if (zone_data) {
        let zone_name = (this.props.step.zone <= 5) ? 'zone ' + this.props.step.zone + ' - ' + zone_data.name + '' : zone_data.name;
        let zone_details = <span>({zone_data.power_min}-{zone_data.power_max} watts)</span>;
        target_fields = <span>in {zone_name} {zone_details} </span>;
      }
      else {
        let zone_name = 'zone ' + this.props.step.zone;
        target_fields = <span style={{color: 'red', cursor: 'pointer'}} onClick={this.props.handleOpenZoneSettings}>in {zone_name} – missing zone data in profile</span>
      }

      duration_field = <span className="structured_training__step-duration">{this.getDurationFormatted()}</span>;

    }

    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE) {

      target_fields = (
        <span>
          <span>at</span>&nbsp;
          <span className="structured_training__step-target-value">{this.props.step.getTargetHeartRate()}</span>&nbsp;
          <span className="structured_training__step-target-unit">bpm</span>
         </span>
      );

      duration_field = <span className="structured_training__step-duration">{this.getDurationFormatted()}</span>;

    }

    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE_BY_ZONE) {

      let zone_data = this.props.step.getZoneData(this.props.athlete_data?.training_zones, this.props.discipline);
      if (zone_data) {
        let zone_details = <span>({zone_data.heart_rate_min}-{zone_data.heart_rate_max} bpm)</span>;
        let zone_name = (this.props.step.zone <= 5) ? 'zone ' + this.props.step.zone + ' - ' + zone_data.name + '' : zone_data.name;
        target_fields = <span>in {zone_name} {zone_details} </span>;
      }
      else {
        target_fields = <span style={{color: 'red', cursor: 'pointer'}} onClick={this.props.handleOpenZoneSettings}>missing zone data in profile</span>
      }

      duration_field = <span className="structured_training__step-duration">{this.getDurationFormatted()}</span>;

    }

    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED) {

      let value;
      let unit;
      if (this.props.distance_unit_preference == 'kilometers') {
        value = formatTime(this.props.step.getTargetPaceSecondsPerKilometer());
        unit = 'min/km';
      }
      else {
        value = formatTime(this.props.step.getTargetPaceSecondsPerMile());
        unit = 'min/mile';
      }

      target_fields = (
        <span>
          <span>at</span>&nbsp;
          <span className="structured_training__step-target-value">{value}</span>&nbsp;
          <span className="structured_training__step-target-unit">{unit}</span>
        </span>
      );

      duration_field = <span className="structured_training__step-duration">{this.getDurationFormatted()}</span>;

    }

    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED_BY_ZONE) {

      let zone_data = this.props.step.getZoneData(this.props.athlete_data?.training_zones, this.props.discipline);
      if (zone_data) {
        let value_min;
        let value_max;
        let unit;
        if (this.props.distance_unit_preference == 'kilometers') {
          value_min = formatTime(this.props.step.getTargetPaceSecondsPerKilometer(this.props.athlete_data?.training_zones, this.props.discipline, 'min'));
          value_max = formatTime(this.props.step.getTargetPaceSecondsPerKilometer(this.props.athlete_data?.training_zones, this.props.discipline, 'max'));
          unit = 'min/km';
        }
        else {
          value_min = formatTime(this.props.step.getTargetPaceSecondsPerMile(this.props.athlete_data?.training_zones, this.props.discipline, 'min'));
          value_max = formatTime(this.props.step.getTargetPaceSecondsPerMile(this.props.athlete_data?.training_zones, this.props.discipline, 'max'));
          unit = 'min/mile';
        }

        let zone_name = (this.props.step.zone <= 5) ? 'zone ' + this.props.step.zone + ' - ' + zone_data.name + '' : zone_data.name;

        let zone_details = <span>({value_min}-{value_max} {unit})</span>;

        target_fields = <span>in {zone_name} {zone_details} </span>;
      }
      else {
        target_fields = <span style={{color: 'red', cursor: 'pointer'}} onClick={this.props.handleOpenZoneSettings}>missing zone data in profile</span>
      }

      duration_field = <span className="structured_training__step-duration">{this.getDurationFormatted()}</span>;
    }

    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_OPEN) {

      if (this.props.discipline != WORKOUT_DISCIPLINES.SWIMMING) {
        target_fields = (
          <span>
            <span>at</span>&nbsp;
            <span className="structured_training__step-target-value">no target intensity</span>&nbsp;
          </span>
        );
      }

      duration_field = <span className="structured_training__step-duration">{this.getDurationFormatted()}</span>;

    }

    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_NOTE) {

      target_fields = (
        get_text_as_jsx(this.props.step.note)
      );

    }

    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_SWIM_STROKE) {
      duration_field = <span className="structured_training__step-duration">{this.getDurationFormatted()}</span>;
    }

    return (
      <div style={{margin: '5px 0px 5px 0px'}}>
        {duration_field} {target_fields}
      </div>
    )
  }
}

export class StructuredTrainingStepEditor extends React.Component {

  constructor(props) {
    super(props);
    this.step_has_focus = false;
    this.firstFocusableElement = React.createRef();
  }

  componentDidUpdate = (prevProps) => {
    if (prevProps.step_is_active !== this.props.step_is_active) {

      if (this.props.step_is_active) {

        if (!this.step_has_focus && this.firstFocusableElement.current) {
          // Do not try to update the focus of the step if the step already
          // has focus, otherwise the wrong input element within the step may
          // be given focus.
          this.firstFocusableElement.current.focus();
        }

      }

      this.step_has_focus = this.props.step_is_active;

    }
  }

  handleTimeValueChanged = (value) => {
    this.props.step.duration_value = value;
    this.registerStepUpdate(this.props.step);
  }

  handleDurationValueChanged = (event) => {
    let value = event.target.value;
    if (value == '') {
      this.props.step.duration_value = null;
    }
    else if (value < 0) {
      // Don't allow values under 0.
      return;
    }
    else if (this.props.step.duration_type == STEP_CONSTANTS.DURATION_TYPE_DISTANCE) {
      if (this.props.distance_unit_preference == 'meters') {
        this.props.step.duration_value = value * 100;
      }
      else {
        this.props.step.duration_value = value * 100000;
      }
    }
    this.registerStepUpdate(this.props.step);
  }

  getDurationValue = () => {
    if (this.props.step.duration_value == null) {
      return '';
    }
    else if (this.props.step.duration_type == STEP_CONSTANTS.DURATION_TYPE_TIME) {
      return this.props.step.duration_value;
    }
    else if (this.props.step.duration_type == STEP_CONSTANTS.DURATION_TYPE_DISTANCE) {
      if (this.props.distance_unit_preference == 'meters') {
        return this.props.step.duration_value / 100;
      }
      else {
        return this.props.step.duration_value / 100000;
      }
    }
    return '';
  }

  getDurationMetric = () => {
    if (this.props.step.duration_type == STEP_CONSTANTS.DURATION_TYPE_DISTANCE) {
      // At the moment we are only supporting metric values.
      if (this.props.distance_unit_preference == 'meters') {
        return 'meters';
      }
      else {
        return 'km';
      }
    }
    else if (this.props.step.duration_type == STEP_CONSTANTS.DURATION_TYPE_TIME) {
      return 'seconds';
    }
  }

  getTargetValueHigh = () => {
    if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER) {
      if (!isNaN(this.props.step.custom_target_value_high) && this.props.step.custom_target_value_high !== null) {
        return this.props.step.custom_target_value_high;
      }
    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_PERCENTAGE_FTP) {
      if (this.props.step.custom_target_value_high < 1000 && this.props.step.custom_target_value_high >= 0) {
        return this.props.step.custom_target_value_high;
      }
    }
    return '';
  }

  handleTargetValueHighChanged = (event) => {
    this.props.step.custom_target_value_high = parseInt(event.target.value);
    this.registerStepUpdate(this.props.step);
  }

  handleProceedOffValueChanged = (value) => {
    this.props.step.setProceedOffSeconds(value)
    this.registerStepUpdate(this.props.step);
  }

  handleStepRestIntervalChanged = (value) => {
    this.props.step.setStepRestInterval(value)
    this.registerStepUpdate(this.props.step);
  }

  handleStepRepeatValueChanged = (event) => {
    this.props.step.setIndividualStepRepetitions(event.target.value)
    this.registerStepUpdate(this.props.step);
  }

  handleZoneChange = (event) => {
    this.props.step.zone = event.target.value;
    this.registerStepUpdate(this.props.step);
  }

  handleNotesChanged = (value) => {
    this.props.step.note = value;
    this.registerStepUpdate(this.props.step);
  }

  registerStepUpdate = (step) => {
    step.markSectionHasChanges();
    this.props.updateStructuredTrainingSteps([step]);
    this.props.formChanged();
  }

  getTargetMetric = () => {
    if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER) {
      return 'watts';
    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_PERCENTAGE_FTP) {
      return 'ftp_percentage';
    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE) {
      return 'power_zone';
    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_NOTE) {
      return 'note';
    }
  }

  handleDeleteStep = () => {
    this.props.removeStep(this.props.step.index)
  }

  handleMoveStepUp = () => {

    if (!this.props.prev_step) {
      return;
    }

    let new_index = this.props.prev_step.index;
    let old_index = this.props.step.index;
    this.props.step.index = new_index;
    this.props.step.markSectionHasChanges();
    this.props.prev_step.index = old_index;
    this.props.updateStructuredTrainingSteps([this.props.step, this.props.prev_step]);
    this.props.formChanged();

  }

  handleMoveStepDown = () => {

    if (!this.props.next_step) {
      return;
    }

    let new_index = this.props.next_step.index;
    let old_index = this.props.step.index;
    this.props.step.index = new_index;
    this.props.step.markSectionHasChanges();
    this.props.next_step.index = old_index;
    this.props.next_step.markSectionHasChanges();
    this.props.updateStructuredTrainingSteps([this.props.step, this.props.next_step]);
    this.props.formChanged();

  }

  handleFocus = () => {

    // Record that the step now has focus. This is used to stop setting
    // the focus on another input element within the step after updating
    // the active step value.
    this.step_has_focus = true;

    this.props.setActiveStep(this.props.step.id);
  }

  render () {

    let duration_metric = this.getDurationMetric();
    let target_metric = this.getTargetMetric();

    let duration_field;
    if (duration_metric == 'km' || duration_metric == 'meters') {

      let unit_label;
      let field_width;
      if (duration_metric == 'meters') {
        unit_label =  'm';
        field_width = '2.2em';
      }
      else {
        unit_label = 'km';
        field_width = '2em';
      }

      duration_field = (
        <span>
          <input
            type="number"
            className="structured_training__step-duration-value session-form__number-field"
            value={this.getDurationValue()}
            onChange={this.handleDurationValueChanged}
            style={{background: INPUT_BACKGROUND_COLOR, border: '0', padding: '2px', margin: '2px 0', width: field_width}}
            ref={this.firstFocusableElement} />&nbsp;
          <span className="structured_training__step-duration-type">{unit_label}</span>
        </span>
      );
    }
    else if (duration_metric == 'seconds') {
      duration_field = (
        <DurationInput
          className="structured_training__step-duration-value session-form__number-field"
          value={this.getDurationValue()}
          onChange={this.handleTimeValueChanged}
          style={{background: INPUT_BACKGROUND_COLOR, border: '0', padding: '2px', margin: '2px 0'}}
          format="HH:MM:SS"
          ref={this.firstFocusableElement}
        />
      );
    }

    let swim_stroke_field;
    let equipment_field;
    let rest_field;
    let step_level_repeat_field;
    let step_proceed_off_field;
    let step_rest_interval_field;
    let edit_step_popup_button;
    if (this.props.discipline == WORKOUT_DISCIPLINES.SWIMMING) {

      if (this.props.step.intensity == INTENSITY.REST) {
        rest_field = <span>rest</span>
      }
      else {

        step_level_repeat_field = (
          <span>
            <input
              type="number"
              className="structured_training__step-repeat-value session-form__number-field"
              value={this.props.step.getIndividualStepRepetitions() || ''}
              onChange={this.handleStepRepeatValueChanged}
              style={{background: INPUT_BACKGROUND_COLOR, border: '0', padding: '2px', margin: '2px 0', width: '1.2em'}}
            />&nbsp;
            <span className="structured_training__step-repeat-x">x</span>&nbsp;
          </span>
        );

        if (this.props.step.getSwimStrokeAsId() !== null) {
          swim_stroke_field = (
            <StepSwimStrokeWidget
              step={this.props.step}
              updateStructuredTrainingSteps={this.props.updateStructuredTrainingSteps}
              formChanged={this.props.formChanged}
              getScrollPosition={this.props.getScrollPosition}
            />
          );
        }

        if (this.props.step.equipment_ids && this.props.step.equipment_ids[0] !== -1) {
          equipment_field = (
            <span>
              <span>with</span>&nbsp;
              <StepSwimEquipmentWidget
                step={this.props.step}
                updateStructuredTrainingSteps={this.props.updateStructuredTrainingSteps}
                formChanged={this.props.formChanged}
                getScrollPosition={this.props.getScrollPosition}
              />
            </span>
          );
        }

        if (typeof(this.props.step.proceed_off_seconds) !== 'undefined' && this.props.step.proceed_off_seconds !== null) {
          step_proceed_off_field = <span>
            <span>off</span>&nbsp;
            <DurationInput
              className="structured_training__step-proceed-off session-form__number-field"
              value={this.props.step.getProceedOffSeconds()}
              onChange={this.handleProceedOffValueChanged}
              style={{background: INPUT_BACKGROUND_COLOR, border: '0', padding: '2px', margin: '2px 0', width: '2.6em'}}
              format="MM:SS"
            />
          </span>;
        }

        if (typeof(this.props.step.step_rest_interval) !== 'undefined' && this.props.step.step_rest_interval !== null) {
          step_rest_interval_field = <span>
            <DurationInput
              className="structured_training__step-rest-interval session-form__number-field"
              value={this.props.step.getStepRestInterval()}
              onChange={this.handleStepRestIntervalChanged}
              style={{background: INPUT_BACKGROUND_COLOR, border: '0', padding: '2px', margin: '2px 0', width: '2.6em'}}
              format="MM:SS"
            />&nbsp;
            <span>rest</span>
          </span>;
        }

        edit_step_popup_button = (
          <EditStepPopupWidget
            step={this.props.step}
            updateStructuredTrainingSteps={this.props.updateStructuredTrainingSteps}
            formChanged={this.props.formChanged}
            getScrollPosition={this.props.getScrollPosition}
          />
        );

      }
    }

    let target_fields;
    if (target_metric == 'note') {
      target_fields = (
        <ResizableTextarea
          value={this.props.step.note}
          onChange={this.handleNotesChanged}
          line_height={21}
          style={{width: '100%', boxSizing: 'border-box', fontFamily: 'helvetica, arial, sans-serif', borderRadius: '3px', marginTop: '2px'}}
        />
      );
    }
    else if (this.props.step.intensity != INTENSITY.REST && this.props.discipline != WORKOUT_DISCIPLINES.SWIMMING) {
      target_fields = (
        <span key="rendered_step">
          <StepTargetWidget
            discipline={this.props.discipline}
            target_type_category={this.props.target_type_category}
            step={this.props.step}
            custom_zones={this.props.custom_zones}
            distance_unit_preference={this.props.distance_unit_preference}
            updateStructuredTrainingSteps={this.props.updateStructuredTrainingSteps}
            formChanged={this.props.formChanged}
            getScrollPosition={this.props.getScrollPosition}
          />
        </span>
      );
    }

    let buttons;
    let zone_data = this.props.step.getZoneData(this.props.athlete_data?.training_zones, this.props.discipline);
    var up_button_class = (this.props.prev_step) ? 'icon up-arrow-icon icon_active move-step-up-button' : 'icon up-arrow-icon icon_disabled move-step-up-button';
    var down_button_class = (this.props.next_step) ? 'icon down-arrow-icon icon_active move-step-down-button' : 'icon down-arrow-icon icon_disabled move-step-down-button';
    if (this.props.step_is_active) {
      buttons = (
        <span className="structured-training__step-buttons small-buttons-group" style={{float: 'right', marginTop: '4px'}}>
          <span className={up_button_class} onClick={this.handleMoveStepUp}>up</span>
          <span className={down_button_class} onClick={this.handleMoveStepDown}>down</span>
          <span className="icon delete-icon" onClick={this.handleDeleteStep}>delete</span>
        </span>
      );
    }
    else if (this.props.step.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE) {
      if (zone_data) {
        buttons = <span>{zone_data.power_min}-{zone_data.power_max} watts</span>
      }
      else {
        buttons = <span style={{color: 'red', cursor: 'pointer'}} onClick={this.props.handleOpenZoneSettings}>missing zone data</span>
      }
    }

    let zone = this.props.step.get1to5Zone(this.props.athlete_data?.training_zones, this.props.discipline);
    if (!zone) {
      zone = 0;
    }

    const drag_handle_styles = {
      width: '12px',
      height: '19px',
      display: 'block',
      borderRadius: '2px',
      cursor: 'move',
      margin: '2px 3px 0px 0px',
      float: 'left',
    }

    const drag_handle = <div className={"step-section__step__drag-handle step-section__step__drag-handle__intensity-" + zone} style={drag_handle_styles}></div>;

    let controls;
    if (target_metric == 'note') {
      controls = <div>{drag_handle}{buttons}{duration_field}{target_fields} </div>
    }
    else {
      controls = <div>{drag_handle}{step_level_repeat_field}{duration_field} {rest_field}{swim_stroke_field}{equipment_field}{target_fields}{step_proceed_off_field}{step_rest_interval_field}{edit_step_popup_button} {buttons}</div>
    }

    return (
      <div className={'step-section__step step-section__step-' + this.props.index} data-step-index={this.props.index} style={{margin: '0px 0px 0px 0px'}} onFocus={this.handleFocus}>
        {controls}
      </div>
    )

  }
}